import { useState, useEffect, useCallback, useRef } from 'react'
import { flushSync } from 'react-dom'
import { apiService } from '../services/apiService'
import type { ShipmentItem } from '../types'

// Debounce utility for reducing API calls
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

const metalInputValue = (value: unknown) => (
  value === null || value === undefined || value === '' ? '' : String(value)
)

const parseMetalInput = (value: string) => {
  if (value.trim() === '') return null
  const numericValue = Number(value)
  return Number.isFinite(numericValue) ? numericValue : null
}

type MetalField = 'metal_aluminum' | 'metal_copper' | 'metal_steel'

type SavedASINRecommendation = {
  country_of_origin?: string | null
  manufacturing_address?: ManufacturingAddress | null
  manufacturing_address_id?: number | null
  hts_code?: string | null
  hts_description?: string | null
  hts_confidence?: number | null
  customs_description?: string | null
  cogs_cad?: number | null
  is_food?: boolean | number | null
  fda_product_code?: string | null
  is_cosmetic_product?: boolean | number | null
  fda_classification?: string | null
  metal_aluminum?: number | null
  metal_copper?: number | null
  metal_steel?: number | null
}

type ConfirmedASINSubmission = {
  shipment_id: string
  asin: string
  sku?: string | null
  description?: string | null
  country_of_origin?: string | null
  hts_code?: string | null
  hts_description?: string | null
  customs_description?: string | null
  is_food?: boolean | number | null
  fda_product_code?: string | null
  is_cosmetic_product?: boolean | number | null
  fda_classification?: string | null
  metal_aluminum?: number | null
  metal_copper?: number | null
  metal_steel?: number | null
  product_value_usd?: number | string | null
  product_quantity?: number | null
  ssb_uuid?: string | null
  ssb_short_id?: string | null
  submitted_at?: string | null
}

type YYZASINOccurrence = {
  shipment_id: string
  shipment_name?: string | null
  status?: string | null
  created_at?: string | null
  shipped_date?: string | null
  sku?: string | null
  title?: string | null
  quantity?: number | null
}

interface ManufacturingAddress {
  id?: number
  name: string
  street_address: string
  city: string
  state?: string
  country: string
  zip_code?: string
  reasoning?: string
  confidence?: number
  sources?: Array<{
    type: string
    url: string
    description: string
  }>
  research_method?: string
  verification_status?: string
  facility_type?: string
  is_food?: boolean
  fda_registration_id?: string
}

interface ClientASINData {
  asinData: SavedASINRecommendation | null
  manufacturingAddresses: ManufacturingAddress[]
}

interface SSBRateResult {
  dutyAmount: number
  dutyAmountCAD?: number
  dutyAmountUSD?: number
  dutyRate: string
  dutyRatePercent?: number
  verified?: boolean
  cached?: boolean
  source?: string
  currency?: string
  htsCode?: string
  countryOfOrigin?: string
  timestamp: string
  cachedAt?: string | null
  cacheExpiresAt?: string
}

interface ShipmentItemRowProps {
  item: ShipmentItem
  shipmentId?: string
  manufacturingAddresses?: ManufacturingAddress[]
  validationErrors?: string[]
  exchangeRate?: { cadToUsd: number, usdToCad: number } | null  // OPTIMIZED: Pass from parent to avoid per-item API calls
  externalSsbVerifiedData?: SSBRateResult | null
  onUpdate: (updates: Partial<ShipmentItem>) => void
  onNewAddressSaved?: (address: ManufacturingAddress & { id: number }) => void
  onDutyCalculated?: (dutyAmount: number) => void
  onSSBRateCalculated?: (result: SSBRateResult | null) => void
  isStaff?: boolean
  clientId?: string
}

const COUNTRIES = [
  { code: 'AD', name: 'Andorra' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'AF', name: 'Afghanistan' },
  { code: 'AG', name: 'Antigua and Barbuda' },
  { code: 'AI', name: 'Anguilla' },
  { code: 'AL', name: 'Albania' },
  { code: 'AM', name: 'Armenia' },
  { code: 'AO', name: 'Angola' },
  { code: 'AQ', name: 'Antarctica' },
  { code: 'AR', name: 'Argentina' },
  { code: 'AS', name: 'American Samoa' },
  { code: 'AT', name: 'Austria' },
  { code: 'AU', name: 'Australia' },
  { code: 'AW', name: 'Aruba' },
  { code: 'AX', name: 'Åland Islands' },
  { code: 'AZ', name: 'Azerbaijan' },
  { code: 'BA', name: 'Bosnia and Herzegovina' },
  { code: 'BB', name: 'Barbados' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BF', name: 'Burkina Faso' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'BI', name: 'Burundi' },
  { code: 'BJ', name: 'Benin' },
  { code: 'BL', name: 'Saint Barthélemy' },
  { code: 'BM', name: 'Bermuda' },
  { code: 'BN', name: 'Brunei' },
  { code: 'BO', name: 'Bolivia' },
  { code: 'BQ', name: 'Caribbean Netherlands' },
  { code: 'BR', name: 'Brazil' },
  { code: 'BS', name: 'Bahamas' },
  { code: 'BT', name: 'Bhutan' },
  { code: 'BV', name: 'Bouvet Island' },
  { code: 'BW', name: 'Botswana' },
  { code: 'BY', name: 'Belarus' },
  { code: 'BZ', name: 'Belize' },
  { code: 'CA', name: 'Canada' },
  { code: 'CC', name: 'Cocos (Keeling) Islands' },
  { code: 'CD', name: 'Congo (Democratic Republic)' },
  { code: 'CF', name: 'Central African Republic' },
  { code: 'CG', name: 'Congo (Republic)' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'CI', name: 'Côte d\'Ivoire' },
  { code: 'CK', name: 'Cook Islands' },
  { code: 'CL', name: 'Chile' },
  { code: 'CM', name: 'Cameroon' },
  { code: 'CN', name: 'China' },
  { code: 'CO', name: 'Colombia' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'CU', name: 'Cuba' },
  { code: 'CV', name: 'Cape Verde' },
  { code: 'CW', name: 'Curaçao' },
  { code: 'CX', name: 'Christmas Island' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'DE', name: 'Germany' },
  { code: 'DJ', name: 'Djibouti' },
  { code: 'DK', name: 'Denmark' },
  { code: 'DM', name: 'Dominica' },
  { code: 'DO', name: 'Dominican Republic' },
  { code: 'DZ', name: 'Algeria' },
  { code: 'EC', name: 'Ecuador' },
  { code: 'EE', name: 'Estonia' },
  { code: 'EG', name: 'Egypt' },
  { code: 'EH', name: 'Western Sahara' },
  { code: 'ER', name: 'Eritrea' },
  { code: 'ES', name: 'Spain' },
  { code: 'ET', name: 'Ethiopia' },
  { code: 'FI', name: 'Finland' },
  { code: 'FJ', name: 'Fiji' },
  { code: 'FK', name: 'Falkland Islands' },
  { code: 'FM', name: 'Micronesia' },
  { code: 'FO', name: 'Faroe Islands' },
  { code: 'FR', name: 'France' },
  { code: 'GA', name: 'Gabon' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'GD', name: 'Grenada' },
  { code: 'GE', name: 'Georgia' },
  { code: 'GF', name: 'French Guiana' },
  { code: 'GG', name: 'Guernsey' },
  { code: 'GH', name: 'Ghana' },
  { code: 'GI', name: 'Gibraltar' },
  { code: 'GL', name: 'Greenland' },
  { code: 'GM', name: 'Gambia' },
  { code: 'GN', name: 'Guinea' },
  { code: 'GP', name: 'Guadeloupe' },
  { code: 'GQ', name: 'Equatorial Guinea' },
  { code: 'GR', name: 'Greece' },
  { code: 'GS', name: 'South Georgia and South Sandwich Islands' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'GU', name: 'Guam' },
  { code: 'GW', name: 'Guinea-Bissau' },
  { code: 'GY', name: 'Guyana' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'HM', name: 'Heard Island and McDonald Islands' },
  { code: 'HN', name: 'Honduras' },
  { code: 'HR', name: 'Croatia' },
  { code: 'HT', name: 'Haiti' },
  { code: 'HU', name: 'Hungary' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IL', name: 'Israel' },
  { code: 'IM', name: 'Isle of Man' },
  { code: 'IN', name: 'India' },
  { code: 'IO', name: 'British Indian Ocean Territory' },
  { code: 'IQ', name: 'Iraq' },
  { code: 'IR', name: 'Iran' },
  { code: 'IS', name: 'Iceland' },
  { code: 'IT', name: 'Italy' },
  { code: 'JE', name: 'Jersey' },
  { code: 'JM', name: 'Jamaica' },
  { code: 'JO', name: 'Jordan' },
  { code: 'JP', name: 'Japan' },
  { code: 'KE', name: 'Kenya' },
  { code: 'KG', name: 'Kyrgyzstan' },
  { code: 'KH', name: 'Cambodia' },
  { code: 'KI', name: 'Kiribati' },
  { code: 'KM', name: 'Comoros' },
  { code: 'KN', name: 'Saint Kitts and Nevis' },
  { code: 'KP', name: 'North Korea' },
  { code: 'KR', name: 'South Korea' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'KY', name: 'Cayman Islands' },
  { code: 'KZ', name: 'Kazakhstan' },
  { code: 'LA', name: 'Laos' },
  { code: 'LB', name: 'Lebanon' },
  { code: 'LC', name: 'Saint Lucia' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'LR', name: 'Liberia' },
  { code: 'LS', name: 'Lesotho' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LY', name: 'Libya' },
  { code: 'MA', name: 'Morocco' },
  { code: 'MC', name: 'Monaco' },
  { code: 'MD', name: 'Moldova' },
  { code: 'ME', name: 'Montenegro' },
  { code: 'MF', name: 'Saint Martin' },
  { code: 'MG', name: 'Madagascar' },
  { code: 'MH', name: 'Marshall Islands' },
  { code: 'MK', name: 'North Macedonia' },
  { code: 'ML', name: 'Mali' },
  { code: 'MM', name: 'Myanmar' },
  { code: 'MN', name: 'Mongolia' },
  { code: 'MO', name: 'Macao' },
  { code: 'MP', name: 'Northern Mariana Islands' },
  { code: 'MQ', name: 'Martinique' },
  { code: 'MR', name: 'Mauritania' },
  { code: 'MS', name: 'Montserrat' },
  { code: 'MT', name: 'Malta' },
  { code: 'MU', name: 'Mauritius' },
  { code: 'MV', name: 'Maldives' },
  { code: 'MW', name: 'Malawi' },
  { code: 'MX', name: 'Mexico' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'MZ', name: 'Mozambique' },
  { code: 'NA', name: 'Namibia' },
  { code: 'NC', name: 'New Caledonia' },
  { code: 'NE', name: 'Niger' },
  { code: 'NF', name: 'Norfolk Island' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'NI', name: 'Nicaragua' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NO', name: 'Norway' },
  { code: 'NP', name: 'Nepal' },
  { code: 'NR', name: 'Nauru' },
  { code: 'NU', name: 'Niue' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'OM', name: 'Oman' },
  { code: 'PA', name: 'Panama' },
  { code: 'PE', name: 'Peru' },
  { code: 'PF', name: 'French Polynesia' },
  { code: 'PG', name: 'Papua New Guinea' },
  { code: 'PH', name: 'Philippines' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'PL', name: 'Poland' },
  { code: 'PM', name: 'Saint Pierre and Miquelon' },
  { code: 'PN', name: 'Pitcairn' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'PS', name: 'Palestine' },
  { code: 'PT', name: 'Portugal' },
  { code: 'PW', name: 'Palau' },
  { code: 'PY', name: 'Paraguay' },
  { code: 'QA', name: 'Qatar' },
  { code: 'RE', name: 'Réunion' },
  { code: 'RO', name: 'Romania' },
  { code: 'RS', name: 'Serbia' },
  { code: 'RU', name: 'Russia' },
  { code: 'RW', name: 'Rwanda' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'SB', name: 'Solomon Islands' },
  { code: 'SC', name: 'Seychelles' },
  { code: 'SD', name: 'Sudan' },
  { code: 'SE', name: 'Sweden' },
  { code: 'SG', name: 'Singapore' },
  { code: 'SH', name: 'Saint Helena' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'SJ', name: 'Svalbard and Jan Mayen' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SL', name: 'Sierra Leone' },
  { code: 'SM', name: 'San Marino' },
  { code: 'SN', name: 'Senegal' },
  { code: 'SO', name: 'Somalia' },
  { code: 'SR', name: 'Suriname' },
  { code: 'SS', name: 'South Sudan' },
  { code: 'ST', name: 'São Tomé and Príncipe' },
  { code: 'SV', name: 'El Salvador' },
  { code: 'SX', name: 'Sint Maarten' },
  { code: 'SY', name: 'Syria' },
  { code: 'SZ', name: 'Eswatini' },
  { code: 'TC', name: 'Turks and Caicos Islands' },
  { code: 'TD', name: 'Chad' },
  { code: 'TF', name: 'French Southern Territories' },
  { code: 'TG', name: 'Togo' },
  { code: 'TH', name: 'Thailand' },
  { code: 'TJ', name: 'Tajikistan' },
  { code: 'TK', name: 'Tokelau' },
  { code: 'TL', name: 'Timor-Leste' },
  { code: 'TM', name: 'Turkmenistan' },
  { code: 'TN', name: 'Tunisia' },
  { code: 'TO', name: 'Tonga' },
  { code: 'TR', name: 'Turkey' },
  { code: 'TT', name: 'Trinidad and Tobago' },
  { code: 'TV', name: 'Tuvalu' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'TZ', name: 'Tanzania' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'UG', name: 'Uganda' },
  { code: 'UM', name: 'United States Minor Outlying Islands' },
  { code: 'US', name: 'United States' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'UZ', name: 'Uzbekistan' },
  { code: 'VA', name: 'Vatican City' },
  { code: 'VC', name: 'Saint Vincent and the Grenadines' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'VG', name: 'British Virgin Islands' },
  { code: 'VI', name: 'U.S. Virgin Islands' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'VU', name: 'Vanuatu' },
  { code: 'WF', name: 'Wallis and Futuna' },
  { code: 'WS', name: 'Samoa' },
  { code: 'YE', name: 'Yemen' },
  { code: 'YT', name: 'Mayotte' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'ZM', name: 'Zambia' },
  { code: 'ZW', name: 'Zimbabwe' }
]

const parseDutyRate = (dutyRate: string): number => {
  if (!dutyRate || dutyRate === 'N/A' || dutyRate === 'Free' || dutyRate.toLowerCase().includes('free')) {
    return 0
  }
  // Extract percentage from strings like "5.2%", "10%", "$0.05/kg + 3.2%"
  const percentMatch = dutyRate.match(/(\d+(?:\.\d+)?)%/)
  if (percentMatch) {
    return parseFloat(percentMatch[1])
  }
  // If it contains "Free" or "$0" patterns, treat as 0
  if (dutyRate.toLowerCase().includes('free') || dutyRate.includes('$0')) {
    return 0
  }
  return 999 // Default high value for unparseable rates
}

const normalizeHTSCode = (value?: string | null): string => {
  return String(value || '').replace(/\D/g, '')
}

const getHTSOptionCode = (option: any): string => {
  return option?.formattedHtsCode || option?.htsCode || option?.TariffNumFormatted || option?.TariffNum || ''
}

// CUSMA (USMCA) countries that may qualify for duty-free treatment
const CUSMA_COUNTRIES = ['US', 'CA', 'MX']

const isCUSMAEligible = (countryCode: string): boolean => {
  return CUSMA_COUNTRIES.includes(countryCode?.toUpperCase())
}

const formatDutyRateWithCUSMA = (dutyRate: string, countryOfOrigin: string): { display: string, tooltip?: string } => {
  if (!dutyRate || dutyRate === 'N/A' || dutyRate === 'Free') {
    return { display: dutyRate || 'N/A' }
  }

  const isCUSMA = isCUSMAEligible(countryOfOrigin)
  const dutyValue = parseDutyRate(dutyRate)

  if (isCUSMA && dutyValue > 0) {
    return {
      display: `${dutyRate} (CUSMA: 0%)`,
      tooltip: `Standard duty rate is ${dutyRate}, but CUSMA-qualifying goods from ${countryOfOrigin === 'US' ? 'USA' : countryOfOrigin === 'CA' ? 'Canada' : 'Mexico'} may enter duty-free if they meet origin requirements.`
    }
  }

  return { display: dutyRate }
}

// Shared grid template for table header (ShipmentDetails) and rows (ShipmentItemRow)
export const SHIPMENT_TABLE_GRID = '48px minmax(160px, 1.8fr) 42px minmax(100px, 0.8fr) 160px 85px minmax(120px, 1fr) minmax(110px, 0.9fr) minmax(120px, 1fr) minmax(100px, 1fr) minmax(130px, 1.2fr) 90px 32px'

export default function ShipmentItemRow({ item, shipmentId, manufacturingAddresses: propManufacturingAddresses, validationErrors = [], exchangeRate: propExchangeRate, externalSsbVerifiedData, onUpdate, onNewAddressSaved, onDutyCalculated, onSSBRateCalculated, isStaff = false, clientId }: ShipmentItemRowProps) {
  const productHistoryKey = item.asin || item.sku || ''
  const snapshotLocked = item.outboundSnapshot?.status === 'locked' || item.outboundSnapshot?.status === 'submitted'
  const [, setClientData] = useState<ClientASINData | null>(null)
  const [savedASINRecommendation, setSavedASINRecommendation] = useState<SavedASINRecommendation | null>((item as any).savedASINRecommendation || null)
  const [latestConfirmedSubmission, setLatestConfirmedSubmission] = useState<ConfirmedASINSubmission | null>((item as any).latestConfirmedSubmission || null)
  const [confirmedASINHistory, setConfirmedASINHistory] = useState<ConfirmedASINSubmission[]>((item as any).confirmedSubmissionHistory || [])
  const [yyzASINOccurrences, setYyzASINOccurrences] = useState<YYZASINOccurrence[]>([])
  const [asinHistoryLoaded, setAsinHistoryLoaded] = useState(false)
  const [asinHistoryLoading, setAsinHistoryLoading] = useState(false)
  const [manualHistoryOpen, setManualHistoryOpen] = useState(false)
  const [localManufacturingAddresses, setLocalManufacturingAddresses] = useState<ManufacturingAddress[]>(propManufacturingAddresses || [])
  const [, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [htsEvents, setHtsEvents] = useState<any[]>([])
  const [htsHistoryOpen, setHtsHistoryOpen] = useState(false)
  const [htsHistoryLoading, setHtsHistoryLoading] = useState(false)
  // OPTIMIZED: Use preloaded countrySuggestion from item (loaded by complete-data endpoint)
  const [countrySuggestion, setCountrySuggestion] = useState<{ asin: string, countryCode: string, source: string } | null>(
    (item as any).countrySuggestion || null
  )
  // OPTIMIZED: Use exchange rate from parent prop instead of fetching per-item
  const [exchangeRate, setExchangeRate] = useState<{ cadToUsd: number, usdToCad: number } | null>(propExchangeRate || null)

  // Local state for editable fields
  const [countryOfOrigin, setCountryOfOrigin] = useState(item.countryOfOrigin || '')
  const [htsCode, setHtsCode] = useState(item.htsCode || '')
  const [customsDescriptionDraft, setCustomsDescriptionDraft] = useState(item.customsDescription || '')
  const [metalAluminum, setMetalAluminum] = useState(metalInputValue(item.metal_aluminum ?? item.metalAluminum))
  const [metalCopper, setMetalCopper] = useState(metalInputValue(item.metal_copper ?? item.metalCopper))
  const [metalSteel, setMetalSteel] = useState(metalInputValue(item.metal_steel ?? item.metalSteel))
  const [metalAiLoading, setMetalAiLoading] = useState(false)
  // OPTIMIZED: Initialize FDA fields from preloaded item data (from complete-data endpoint)
  const [isFood, setIsFood] = useState(!!(item as any).is_food)
  const [fdaProductCode, setFdaProductCode] = useState((item as any).fdaProductCode || '')
  const [fdaClassifying, setFdaClassifying] = useState(false)
  const [fdaClassificationResult, setFdaClassificationResult] = useState<any>(null)
  const [isCosmetic, setIsCosmetic] = useState(!!(item as any).is_cosmetic_product)
  // Auto-check CUSMA if country of origin is US, CA, or MX
  const [cusmaApplicable, setCusmaApplicable] = useState(
    ['US', 'CA', 'MX'].includes(item.countryOfOrigin?.toUpperCase() || '')
  )

  // STANDARDIZED COGS HANDLING: Use item.declaredValue as single source of truth
  // Remove duplicate cogsCAD state and use item.declaredValue directly

  // Debounced update functions to reduce API calls
  const debouncedUpdateItem = useCallback(
    debounce((updates: Partial<ShipmentItem>) => {
      onUpdate(updates)
    }, 1000), // 1 second delay
    [onUpdate, item.id]
  )

  const debouncedSaveASINData = useCallback(
    debounce(async (data: any) => {
      await saveASINData(data)
    }, 2000), // 2 second delay for database saves
    [item.asin]
  )

  const [manufacturingAddress, setManufacturingAddress] = useState<ManufacturingAddress | undefined>(undefined)
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null)
  const [showNewAddressForm, setShowNewAddressForm] = useState(false)

  // HTS Classification state
  const [showHTSClassification, setShowHTSClassification] = useState(false)
  const [htsClassifying, setHtsClassifying] = useState(false)
  const [htsClassificationResults, setHtsClassificationResults] = useState<any>(null)
  const [selectedHTSOption, setSelectedHTSOption] = useState<any>(null)
  const [loadingMessage, setLoadingMessage] = useState('Searching US customs database...')
  const [aiLookupLoading, setAiLookupLoading] = useState(false)
  const [aiSuggestedAddress, setAiSuggestedAddress] = useState<ManufacturingAddress | null>(null)
  const [countryMismatchError, setCountryMismatchError] = useState<string | null>(null)

  // PGA state
  const [pgaData, setPgaData] = useState<any>(null)
  const [pgaLoading, setPgaLoading] = useState(false)
  const [pgaError, setPgaError] = useState<string | null>(null)
  const [newAddress, setNewAddress] = useState<ManufacturingAddress>({
    name: '',
    street_address: '',
    city: '',
    state: '',
    country: '',
    zip_code: '',
    is_food: false,
    fda_registration_id: undefined
  })

  // HTS validation state
  const [htsValidation, setHtsValidation] = useState<any>(null)
  const [htsValidating, setHtsValidating] = useState(false)

  // SSB Rate Verification state
  const [ssbVerifiedData, setSsbVerifiedData] = useState<SSBRateResult | null>(null)
  const [ssbRateLoading, setSsbRateLoading] = useState(false)

  const toggleHTSHistory = async () => {
    const nextOpen = !htsHistoryOpen
    setHtsHistoryOpen(nextOpen)
    if (!nextOpen || htsEvents.length > 0 || !productHistoryKey) return
    try {
      setHtsHistoryLoading(true)
      const response = await apiService.getHTSChangeEvents(productHistoryKey, {
        clientId: isStaff ? clientId : undefined,
        limit: 25
      })
      if (response.success && response.data) setHtsEvents(response.data)
    } finally {
      setHtsHistoryLoading(false)
    }
  }

  // Row expand/collapse state for detail panel
  const [rowExpanded, setRowExpanded] = useState(false)

  // Sync external SSB data (from bulk SSB check) into local state
  useEffect(() => {
    if (externalSsbVerifiedData) {
      setSsbVerifiedData(externalSsbVerifiedData)
    }
  }, [externalSsbVerifiedData])

  // Ref for HTS dropdown click-outside detection
  const htsDropdownRef = useRef<HTMLDivElement>(null)

  // Close HTS dropdown on click outside
  useEffect(() => {
    if (!showHTSClassification || !htsClassificationResults || htsClassifying) return
    const handleClickOutside = (e: MouseEvent) => {
      if (htsDropdownRef.current && !htsDropdownRef.current.contains(e.target as Node)) {
        setShowHTSClassification(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showHTSClassification, htsClassificationResults, htsClassifying])

  // Helper function to check if country is CUSMA-eligible
  const isCusmaCountry = (countryCode: string) => {
    return ['US', 'CA', 'MX'].includes(countryCode)
  }

  const getRequiredMetalFields = (code: string): MetalField[] => {
    const digits = String(code || '').replace(/\D/g, '')
    if (digits.length < 2) return []
    const chapter = digits.slice(0, 2)
    const heading = digits.slice(0, 4)
    const fullCode = digits.slice(0, 10)
    if (fullCode.startsWith('87168050') && fullCode !== '8716805010') {
      return ['metal_steel']
    }

    const derivativeMetalCodes = [
      '8202390040', '82034060', '82055955', '82057000', '82111000', '82119110', '82119120',
      '82119125', '82119130', '82119140', '82119150', '82119180', '82119220', '82119240',
      '82119260', '82119290', '82119300', '82119410', '82119450', '82119510', '82119550',
      '82119590', '82151000', '82152000', '82159130', '82159160', '82159190', '82159901',
      '82159905', '82159910', '82159915', '82159920', '82159922', '82159924', '82159926',
      '82159930', '82159935', '82159940', '82159945', '82159950', '83021060', '83022000',
      '8302303010', '8302303060', '83024130', '8302416015', '8302416045', '8302416050',
      '8302416080', '83024230', '83024960', '83025000', '83026030', '83026090',
      '8305100050', '83052000', '83063000', '83071060', '83079060', '8309900020',
      '8309900025', '8309900080', '84031000', '84069040', '84079010', '84079090',
      '84109000', '84118180', '84122100', '84122980', '8412909070', '8412909075',
      '84138100', '8413919055', '8413919060', '8413919096', '84143040', '8414596590',
      '84148016', '84149030', '84149041', '84151030', '84151060', '84151090', '84158101',
      '84158201', '84158300', '84159040', '84159080', '8415908010', '8415908020',
      '8415908045', '8415908085', '84181000', '84182100', '84182920', '84183000',
      '84184000', '84189940', '8418998005', '8418998050', '8418998060', '84195010',
      '84195050', '84199010', '84198150', '84221100', '8422900640', '84248990',
      '8424909080', '84254200', '84262000', '84264900', '84269900', '84271040',
      '84271080', '84272040', '84272080', '84279000', '84283200', '84283300',
      '84283900', '84286000', '84287000', '84289003', '84291100', '84291900',
      '84292000', '84293000', '84294000', '84295110', '84295150', '84295210',
      '84295250', '84295910', '84295950', '84312000', '84313100', '84313900',
      '84314100', '84314200', '84314340', '84314380', '84314910', '84314990',
      '84321000', '84329000', '84331100', '84332000', '84335100', '84335900',
      '84339010', '84339050', '84431600', '84501100', '84502000', '84512100',
      '84512900', '8454200010', '8454200060', '84553000', '84559040', '84559080',
      '84571000', '84672200', '84672900', '84678100', '84678950', '84749000',
      '84771030', '84771040', '84771090', '84779025', '8477908601', '84798955',
      '84798965', '8479899599', '84799045', '84799055', '84799065', '84799075',
      '84799085', '84799095', '8479909596', '8480490010', '8480718045', '8480718060',
      '8480799010', '8481909060', '8481909085', '8482105004', '8482105008',
      '8482105012', '8482105016', '8482105024', '8482105028', '8482105032',
      '8482105036', '8482105052', '8482105056', '8482105060', '8482105064',
      '8482105068', '8482200064', '8482200067', '8482200090', '84829905',
      '84829915', '84829925', '84829935', '84829945', '84829965', '8483101010',
      '8483101050', '84831050', '84832040', '84832080', '84833040', '84833080',
      '84834010', '8483405020', '84834090', '84835060', '84835090', '84836040',
      '84836080', '84839020', '84839030', '84839050', '8483905020', '84839070',
      '84839080', '8487900080', '85015360', '85015380', '8501640110', '85022000',
      '85023100', '85030035', '85030065', '85030075', '8503009520', '8503009546',
      '8503009570', '85042100', '85042200', '85043120', '85043140', '85043160',
      '85043200', '85043300', '85043400', '85049020', '85049041', '85049065',
      '85049075', '8504909610', '8504909630', '8504909634', '8504909638',
      '8504909642', '8504909646', '8504909650', '8504909690', '85087000',
      '85098020', '85139020', '85142040', '85142060', '85159020', '85162900',
      '85166040', '85166060', '85169050', '8516908050', '85177100', '85299073',
      '8536908585', '85381000', '8543908885', '85441900', '85444210', '85444220',
      '85444290', '85444910', '85444920', '85444990', '85446020', '85446060',
      '85479000', '8547900020', '8547900030', '8547900040', '86011000', '86012000',
      '86021000', '86029000', '86031000', '86039000', '86040000', '86050000',
      '86061000', '86071100', '86071903', '86071906', '86071912', '86071915',
      '86071990', '86072150', '8607301010', '8607301050', '8607301090',
      '86073050', '86079100', '86079950', '86090000', '87011001', '8701210080',
      '8701220080', '8701230080', '8701240080', '8701290080', '87013010',
      '87013050', '87019110', '87019150', '87019210', '87019250', '87019310',
      '87019350', '87019410', '87019450', '87019510', '87019550', '87021031',
      '87021061', '87031010', '87031050', '87032101', '87051000', '87052000',
      '87060030', '87081030', '87081060', '87082921', '8708295160', '87084030',
      '87084060', '8708806590', '87089210', '87089250', '87089260', '87089275',
      '87089315', '87089330', '87089923', '8708996890', '87089981', '87161000',
      '87163900', '87168010', '87168050', '8716805010', '87169010', '87169030',
      '87169050', '90139080', '9403999020', '94062000', '94069001'
    ]
    if (
      ['72', '73', '74', '76'].includes(chapter) ||
      ['8301', '8302', '8307', '8310', '8311'].includes(heading) ||
      derivativeMetalCodes.some(code => fullCode.startsWith(code) || (digits.length >= 6 && code.startsWith(digits)))
    ) {
      return ['metal_aluminum', 'metal_copper', 'metal_steel']
    }
    return []
  }

  const requiredMetalFields = getRequiredMetalFields(htsCode)
  const metalCompositionRequired = requiredMetalFields.length > 0

  const getMetalFieldValue = (field: MetalField) => {
    if (field === 'metal_aluminum') return metalAluminum
    if (field === 'metal_copper') return metalCopper
    return metalSteel
  }

  const setMetalFieldValue = (field: MetalField, value: string) => {
    if (field === 'metal_aluminum') setMetalAluminum(value)
    if (field === 'metal_copper') setMetalCopper(value)
    if (field === 'metal_steel') setMetalSteel(value)
  }

  const getMetalCompositionValues = (fields = requiredMetalFields) => fields.reduce((values, field) => {
    values[field] = parseMetalInput(getMetalFieldValue(field))
    return values
  }, {} as Record<MetalField, number | null>)

  const saveMetalComposition = async (fields = requiredMetalFields) => {
    const values = getMetalCompositionValues(fields)
    onUpdate({
      ...values,
      ...(values.metal_aluminum !== undefined ? { metalAluminum: values.metal_aluminum } : {}),
      ...(values.metal_copper !== undefined ? { metalCopper: values.metal_copper } : {}),
      ...(values.metal_steel !== undefined ? { metalSteel: values.metal_steel } : {})
    } as Partial<ShipmentItem>)
    await saveASINData(values)
  }

  const suggestMetalComposition = async () => {
    if (!metalCompositionRequired || !htsCode) return
    try {
      setMetalAiLoading(true)
      const response = await apiService.suggestMetalComposition({
        productTitle: customsDescriptionDraft?.trim() || item.description,
        htsCode,
        htsDescription: (item as any).htsDescription || (item as any).hts_description,
        imageUrl: item.imageUrl
      })

      const responseData = (response as any).data || response
      if (!response.success || !responseData?.values) {
        alert(`Metal composition AI failed: ${response.error || 'No suggestion returned'}`)
        return
      }

      const suggestedValues = responseData.values
      const savedValues: Partial<Record<MetalField, number | null>> = {}
      for (const field of requiredMetalFields) {
        const value = suggestedValues[field]
        if (value === undefined || value === null) continue
        const formattedValue = String(value)
        setMetalFieldValue(field, formattedValue)
        savedValues[field] = value
      }

      if (Object.keys(savedValues).length > 0) {
        onUpdate({
          ...savedValues,
          ...(savedValues.metal_aluminum !== undefined ? { metalAluminum: savedValues.metal_aluminum } : {}),
          ...(savedValues.metal_copper !== undefined ? { metalCopper: savedValues.metal_copper } : {}),
          ...(savedValues.metal_steel !== undefined ? { metalSteel: savedValues.metal_steel } : {})
        } as Partial<ShipmentItem>)
        await saveASINData(savedValues)
      }
    } catch (error: any) {
      alert(`Metal composition AI failed: ${error.message || 'Unknown error'}`)
    } finally {
      setMetalAiLoading(false)
    }
  }

  // Load only lightweight data on initial row mount. Saved defaults are preloaded by complete-data;
  // confirmed shipment history is fetched lazily when the row is expanded.
  useEffect(() => {
    if ((item as any).savedASINRecommendation) {
      setSavedASINRecommendation((item as any).savedASINRecommendation)
    }
    if ((item as any).latestConfirmedSubmission) {
      setLatestConfirmedSubmission((item as any).latestConfirmedSubmission)
    }
    if ((item as any).confirmedSubmissionHistory) {
      setConfirmedASINHistory((item as any).confirmedSubmissionHistory)
    }
    if (item.asin) {
      // Only fetch country suggestion if not provided by parent
      if (!(item as any).countrySuggestion && !countrySuggestion) {
        loadCountrySuggestion()
      }
    }
    // Only fetch exchange rate if not provided by parent
    if (!propExchangeRate && !exchangeRate) {
      loadExchangeRate()
    }
  }, [productHistoryKey])

  useEffect(() => {
    if (rowExpanded && productHistoryKey) {
      if (!savedASINRecommendation && !(item as any).savedASINRecommendation) {
        loadClientASINData()
      }
      if (!asinHistoryLoaded && !asinHistoryLoading) {
        loadClientASINHistory()
      }
    }
  }, [rowExpanded, productHistoryKey])

  // Sync exchange rate from parent prop when it changes
  useEffect(() => {
    if (propExchangeRate) {
      setExchangeRate(propExchangeRate)
    }
  }, [propExchangeRate])

  // Sync country suggestion from item when it changes
  useEffect(() => {
    if ((item as any).countrySuggestion && !countrySuggestion) {
      setCountrySuggestion((item as any).countrySuggestion)
    }
  }, [(item as any).countrySuggestion])

  // CRITICAL: Sync countryOfOrigin from item props when complete-data returns enriched data
  useEffect(() => {
    if (item.countryOfOrigin && item.countryOfOrigin !== countryOfOrigin) {
      setCountryOfOrigin(item.countryOfOrigin)
      // Auto-check CUSMA if country is US, CA, or MX
      if (['US', 'CA', 'MX'].includes(item.countryOfOrigin.toUpperCase())) {
        setCusmaApplicable(true)
      }
    }
  }, [item.countryOfOrigin])

  // Validate country match whenever manufacturing address or country of origin changes
  useEffect(() => {
    if (manufacturingAddress && countryOfOrigin) {
      validateCountryMatch(manufacturingAddress.country, countryOfOrigin)
    } else {
      setCountryMismatchError(null)
    }
  }, [manufacturingAddress, countryOfOrigin])

  useEffect(() => {
    if (propManufacturingAddresses) {
      setLocalManufacturingAddresses(propManufacturingAddresses)
    }
  }, [propManufacturingAddresses])

  // Sync selectedAddressId with item.manufacturingAddress when it changes (e.g., from bulk lookup)
  useEffect(() => {
    if (item.manufacturingAddress?.id && item.manufacturingAddress.id !== selectedAddressId) {
      setSelectedAddressId(item.manufacturingAddress.id)
      // Also set the manufacturing address object
      setManufacturingAddress(item.manufacturingAddress as ManufacturingAddress)
    }
  }, [item.manufacturingAddress])

  // Sync htsCode with item.htsCode when it changes (e.g., from bulk HTS classification)
  useEffect(() => {
    if (item.htsCode && item.htsCode !== htsCode) {
      setHtsCode(item.htsCode)
      setHtsValidation(null)
    }
  }, [item.htsCode])

  // Sync FDA fields when item changes (e.g., from bulk FDA classification)
  useEffect(() => {
    if (item.is_food !== undefined && item.is_food !== isFood) {
      setIsFood(!!item.is_food)
    }
  }, [item.is_food])

  useEffect(() => {
    if (item.is_cosmetic_product !== undefined && item.is_cosmetic_product !== isCosmetic) {
      setIsCosmetic(!!item.is_cosmetic_product)
    }
  }, [item.is_cosmetic_product])

  useEffect(() => {
    if (item.fdaProductCode !== undefined && item.fdaProductCode !== fdaProductCode) {
      setFdaProductCode(item.fdaProductCode || '')
    }
  }, [item.fdaProductCode])

  useEffect(() => {
    setCustomsDescriptionDraft(item.customsDescription || '')
  }, [item.customsDescription])

  const loadClientASINData = async () => {
    if (!productHistoryKey) return

    try {
      setLoading(true)
      // Pass clientId if staff user is viewing
      const response = await apiService.getClientASINData(productHistoryKey, isStaff ? clientId : undefined)

      if (response.success && response.data) {
        setClientData(response.data)

        // Update local addresses if not provided by parent
        if (!propManufacturingAddresses && response.data.manufacturingAddresses) {
          setLocalManufacturingAddresses(response.data.manufacturingAddresses)
        }

        // Set initial values from saved data
        if (response.data.asinData) {
          const savedData = response.data.asinData
          setSavedASINRecommendation(savedData)
          const updates: any = {}

          if (savedData.country_of_origin) {
            setCountryOfOrigin(savedData.country_of_origin)
            updates.countryOfOrigin = savedData.country_of_origin
            // Auto-check CUSMA if country is US, CA, or MX
            if (['US', 'CA', 'MX'].includes(savedData.country_of_origin.toUpperCase())) {
              setCusmaApplicable(true)
            }
          }
          if (savedData.hts_code) {
            setHtsCode(savedData.hts_code)
            updates.htsCode = savedData.hts_code
          }
          if (savedData.customs_description !== undefined) {
            const savedDescription = savedData.customs_description || ''
            setCustomsDescriptionDraft(savedDescription)
            updates.customsDescription = savedDescription
          }
          if (savedData.metal_aluminum !== undefined) {
            setMetalAluminum(metalInputValue(savedData.metal_aluminum))
            updates.metal_aluminum = savedData.metal_aluminum
            updates.metalAluminum = savedData.metal_aluminum
          }
          if (savedData.metal_copper !== undefined) {
            setMetalCopper(metalInputValue(savedData.metal_copper))
            updates.metal_copper = savedData.metal_copper
            updates.metalCopper = savedData.metal_copper
          }
          if (savedData.metal_steel !== undefined) {
            setMetalSteel(metalInputValue(savedData.metal_steel))
            updates.metal_steel = savedData.metal_steel
            updates.metalSteel = savedData.metal_steel
          }
          if (savedData.is_food !== undefined) {
            setIsFood(!!savedData.is_food)
          }
          if (savedData.fda_product_code) {
            setFdaProductCode(savedData.fda_product_code)
          }
          if (savedData.is_cosmetic_product !== undefined) {
            setIsCosmetic(!!savedData.is_cosmetic_product)
          }
          if (savedData.fda_classification !== undefined) {
            updates.fdaClassification = savedData.fda_classification
          }
          if (savedData.cogs_cad !== undefined && savedData.cogs_cad !== null) {
            // Update declaredValue directly - no separate cogsCAD state needed
            updates.declaredValue = savedData.cogs_cad
          }
          if (savedData.manufacturing_address) {
            setManufacturingAddress(savedData.manufacturing_address)
            updates.manufacturingAddress = savedData.manufacturing_address

            // Validate country match when loading saved data
            validateCountryMatch(savedData.manufacturing_address.country, savedData.country_of_origin || countryOfOrigin)
          }
          if (savedData.manufacturing_address_id) {
            setSelectedAddressId(savedData.manufacturing_address_id)
          }

          // Update parent component with all loaded values at once
          if (Object.keys(updates).length > 0) {
            onUpdate(updates)
          }
        } else {
          setSavedASINRecommendation(null)
        }
      }
    } catch (error) {
      console.error('Error loading client ASIN data:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadClientASINHistory = async () => {
    if (!productHistoryKey) return

    try {
      setAsinHistoryLoading(true)
      const response = await apiService.getClientASINHistory(productHistoryKey, isStaff ? clientId : undefined, shipmentId)
      if (response.success && response.data) {
        const confirmedSubmissions = response.data.confirmedSubmissions || []
        setConfirmedASINHistory(confirmedSubmissions)
        if (confirmedSubmissions[0]) {
          setLatestConfirmedSubmission(confirmedSubmissions[0])
        }
        setYyzASINOccurrences(response.data.yyzOccurrences || [])
      }
    } catch (error) {
      console.error('Error loading client ASIN history:', error)
      setConfirmedASINHistory([])
      setYyzASINOccurrences([])
    } finally {
      setAsinHistoryLoaded(true)
      setAsinHistoryLoading(false)
    }
  }

  const loadCountrySuggestion = async () => {
    if (!item.asin) return

    try {
      const response = await apiService.getCountrySuggestion(item.asin)

      if (response.success && response.data) {
        setCountrySuggestion(response.data)
      }
    } catch (error) {
      console.error('Error loading country suggestion:', error)
    }
  }

  const applyCountrySuggestion = async () => {
    if (!countrySuggestion) return

    setCountryOfOrigin(countrySuggestion.countryCode)

    // Validate country match with current manufacturing address
    if (manufacturingAddress) {
      validateCountryMatch(manufacturingAddress.country, countrySuggestion.countryCode)
    }

    await saveASINData({ country_of_origin: countrySuggestion.countryCode })
  }

  const hasSavedASINRecommendation = (data?: SavedASINRecommendation | null) => {
    if (!data) return false
    return Boolean(
      data.country_of_origin ||
      data.manufacturing_address ||
      data.hts_code ||
      data.customs_description ||
      data.cogs_cad !== null && data.cogs_cad !== undefined ||
      data.is_food !== null && data.is_food !== undefined ||
      data.fda_product_code ||
      data.is_cosmetic_product !== null && data.is_cosmetic_product !== undefined ||
      data.fda_classification ||
      data.metal_aluminum !== null && data.metal_aluminum !== undefined ||
      data.metal_copper !== null && data.metal_copper !== undefined ||
      data.metal_steel !== null && data.metal_steel !== undefined
    )
  }

  const getCountryName = (code?: string | null) => {
    if (!code) return ''
    return COUNTRIES.find(c => c.code === code)?.name || code
  }

  const cooManualReview = (item as any).cooReviewStatus === 'manual_review'
  const cooAutoFilled = (item as any).cooReviewStatus === 'auto_filled'
  const yyzRecommendedCOO = (item as any).yyzRecommendedCountryOfOrigin

  const getFDAHistoryLabel = (data: SavedASINRecommendation) => {
    const labels: string[] = []
    if (data.is_food) labels.push('Food')
    if (data.is_cosmetic_product) labels.push('Cosmetic')
    if (data.fda_product_code) labels.push(data.fda_product_code)
    if (!labels.length && data.fda_classification) labels.push(data.fda_classification)
    if (!labels.length && (data.is_food === false || data.is_cosmetic_product === false)) labels.push('Not marked FDA/cosmetic')
    return labels.join(' / ')
  }

  const getMetalHistoryLabel = (data: SavedASINRecommendation) => {
    const parts: string[] = []
    if (data.metal_aluminum !== null && data.metal_aluminum !== undefined) parts.push(`Al ${data.metal_aluminum}%`)
    if (data.metal_copper !== null && data.metal_copper !== undefined) parts.push(`Cu ${data.metal_copper}%`)
    if (data.metal_steel !== null && data.metal_steel !== undefined) parts.push(`Steel ${data.metal_steel}%`)
    return parts.join(', ')
  }

  const getManufacturingAddressLabel = (address?: ManufacturingAddress | null) => {
    if (!address) return ''
    return [address.name, address.city, address.state, address.country].filter(Boolean).join(', ')
  }

  const formatHistoryDate = (value?: string | null) => {
    if (!value) return 'Unknown date'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleDateString()
  }

  const normalizeHistoryText = (value?: string | null) => String(value || '').replace(/\s+/g, ' ').trim()
  const normalizeHTS = (value?: string | null) => String(value || '').replace(/\D/g, '')

  type ConfirmedManualOption = {
    entry: ConfirmedASINSubmission
    description: string | null
    htsCode: string | null
    htsDescription: string | null
    fields: string
    occurrences: number
    key: string
  }

  const buildConfirmedManualOptions = (): ConfirmedManualOption[] => {
    if (!isStaff) return []

    const originalDescription = normalizeHistoryText(item.description)
    const activeHTS = htsCode || item.htsCode || ''
    const byPair = new Map<string, ConfirmedManualOption>()

    for (const entry of confirmedASINHistory) {
      const confirmedDescription = normalizeHistoryText(entry.customs_description || entry.description || '')
      const confirmedHTS = entry.hts_code || ''

      const hasManualDescription = Boolean(confirmedDescription && confirmedDescription !== originalDescription)
      const hasDifferentHTS = Boolean(confirmedHTS && normalizeHTS(confirmedHTS) !== normalizeHTS(activeHTS))
      if (!hasManualDescription && !hasDifferentHTS) continue
      const shouldUseHTS = Boolean(confirmedHTS && (hasManualDescription || hasDifferentHTS))

      const key = `${hasManualDescription ? confirmedDescription.toLowerCase() : ''}|${normalizeHTS(confirmedHTS)}`
      const existing = byPair.get(key)
      if (existing) {
        existing.occurrences += 1
        continue
      }

      byPair.set(key, {
        entry,
        description: hasManualDescription ? confirmedDescription : null,
        htsCode: shouldUseHTS ? confirmedHTS : null,
        htsDescription: entry.hts_description || null,
        fields: [
          hasManualDescription ? 'title' : null,
          shouldUseHTS ? 'HTS' : null
        ].filter(Boolean).join(' + '),
        occurrences: 1,
        key
      })
    }

    return Array.from(byPair.values())
  }

  const confirmedManualOptions = buildConfirmedManualOptions()
  const lastConfirmedManualOption = confirmedManualOptions[0] || null

  const applyConfirmedManualOption = async (option: ConfirmedManualOption | null) => {
    if (!option) return

    const updates: any = {}
    const saveOverrides: any = {}

    if (option.description) {
      setCustomsDescriptionDraft(option.description)
      updates.customsDescription = option.description
      saveOverrides.customs_description = option.description
    }

    if (option.htsCode) {
      setHtsCode(option.htsCode)
      setHtsValidation(null)
      updates.htsCode = option.htsCode
      saveOverrides.hts_code = option.htsCode
      if (option.htsDescription) {
        updates.htsDescription = option.htsDescription
        saveOverrides.hts_description = option.htsDescription
      }
    }

    if (Object.keys(updates).length > 0) {
      onUpdate(updates)
      await saveASINData(saveOverrides)
    }
  }

  const applyLastConfirmedManualOption = async () => {
    await applyConfirmedManualOption(lastConfirmedManualOption)
  }

  const applySavedASINRecommendation = () => {
    if (!savedASINRecommendation) return

    const savedData = savedASINRecommendation
    const updates: any = {}

    if (savedData.country_of_origin) {
      setCountryOfOrigin(savedData.country_of_origin)
      updates.countryOfOrigin = savedData.country_of_origin
      setCusmaApplicable(isCusmaCountry(savedData.country_of_origin))
      if (manufacturingAddress) {
        validateCountryMatch(manufacturingAddress.country, savedData.country_of_origin)
      }
    }

    if (savedData.hts_code) {
      setHtsCode(savedData.hts_code)
      setHtsValidation(null)
      updates.htsCode = savedData.hts_code
    }

    if (savedData.customs_description !== undefined) {
      const savedDescription = savedData.customs_description || ''
      setCustomsDescriptionDraft(savedDescription)
      updates.customsDescription = savedDescription
    }

    if (savedData.metal_aluminum !== undefined) {
      setMetalAluminum(metalInputValue(savedData.metal_aluminum))
      updates.metal_aluminum = savedData.metal_aluminum
      updates.metalAluminum = savedData.metal_aluminum
    }

    if (savedData.metal_copper !== undefined) {
      setMetalCopper(metalInputValue(savedData.metal_copper))
      updates.metal_copper = savedData.metal_copper
      updates.metalCopper = savedData.metal_copper
    }

    if (savedData.metal_steel !== undefined) {
      setMetalSteel(metalInputValue(savedData.metal_steel))
      updates.metal_steel = savedData.metal_steel
      updates.metalSteel = savedData.metal_steel
    }

    if (savedData.is_food !== undefined) {
      setIsFood(!!savedData.is_food)
      updates.is_food = !!savedData.is_food
    }

    if (savedData.is_cosmetic_product !== undefined) {
      setIsCosmetic(!!savedData.is_cosmetic_product)
      updates.is_cosmetic_product = !!savedData.is_cosmetic_product
    }

    if (savedData.fda_product_code !== undefined) {
      setFdaProductCode(savedData.fda_product_code || '')
      updates.fdaProductCode = savedData.fda_product_code || ''
    }

    if (savedData.fda_classification !== undefined) {
      updates.fdaClassification = savedData.fda_classification
    }

    if (savedData.cogs_cad !== undefined && savedData.cogs_cad !== null) {
      updates.declaredValue = savedData.cogs_cad
    }

    if (savedData.manufacturing_address) {
      setManufacturingAddress(savedData.manufacturing_address)
      updates.manufacturingAddress = savedData.manufacturing_address
      validateCountryMatch(savedData.manufacturing_address.country, savedData.country_of_origin || countryOfOrigin)
    }

    if (savedData.manufacturing_address_id) {
      setSelectedAddressId(savedData.manufacturing_address_id)
    }

    if (Object.keys(updates).length > 0) {
      onUpdate(updates)
    }
  }

  const loadExchangeRate = async () => {
    try {
      const response = await apiService.getExchangeRate()

      if (response.success && response.data) {
        setExchangeRate(response.data)
      }
    } catch (error) {
      console.error('Error loading exchange rate:', error)
      // Use fallback rate
      setExchangeRate({ cadToUsd: 0.74, usdToCad: 1.35 })
    }
  }

  const saveASINData = async (overrides: {
    country_of_origin?: string,
    manufacturing_address?: ManufacturingAddress,
    manufacturing_address_id?: number | null,
    hts_code?: string | null,
    hts_description?: string | null,
    hts_confidence?: number | null,
    cogs_cad?: number | null,
    is_food?: boolean,
    fda_product_code?: string | null,
    is_cosmetic_product?: boolean,
    fda_classification?: string | null,
    customs_description?: string | null,
    metal_aluminum?: number | null,
    metal_copper?: number | null,
    metal_steel?: number | null
  } = {}) => {
    if (!productHistoryKey) return

    try {
      setSaving(true)

      const dataToSave: Record<string, any> = Object.fromEntries(
        Object.entries(overrides).filter(([, value]) => value !== undefined)
      )
      if (shipmentId) {
        dataToSave.outbound_id = shipmentId
        dataToSave.change_source = 'manual_ui'
      }

      if (dataToSave.hts_code !== undefined && dataToSave.hts_code !== null && String(dataToSave.hts_code).trim() !== '') {
        const digits = String(dataToSave.hts_code).replace(/\D/g, '')
        if (digits.length !== 10) {
          alert(`HTS defaults must contain exactly 10 digits. This ${digits.length}-digit code was not saved.`)
          return
        }
        dataToSave.hts_code = `${digits.slice(0, 4)}.${digits.slice(4, 6)}.${digits.slice(6, 8)}.${digits.slice(8, 10)}`
      }

      const response = await apiService.saveClientASINData(productHistoryKey, dataToSave, isStaff ? clientId : undefined)

      if (response.success) {
        const itemUpdates: any = {}
        if (dataToSave.country_of_origin !== undefined) itemUpdates.countryOfOrigin = dataToSave.country_of_origin
        if (dataToSave.hts_code !== undefined) itemUpdates.htsCode = dataToSave.hts_code || ''
        if (dataToSave.hts_description !== undefined) itemUpdates.htsDescription = dataToSave.hts_description || ''
        if (dataToSave.manufacturing_address !== undefined) itemUpdates.manufacturingAddress = dataToSave.manufacturing_address
        if (dataToSave.cogs_cad !== undefined) itemUpdates.declaredValue = dataToSave.cogs_cad ?? 0
        if (dataToSave.is_food !== undefined) itemUpdates.is_food = dataToSave.is_food
        if (dataToSave.is_cosmetic_product !== undefined) itemUpdates.is_cosmetic_product = dataToSave.is_cosmetic_product
        if (dataToSave.fda_product_code !== undefined) itemUpdates.fdaProductCode = dataToSave.fda_product_code
        if (dataToSave.fda_classification !== undefined) itemUpdates.fdaClassification = dataToSave.fda_classification
        if (dataToSave.customs_description !== undefined) itemUpdates.customsDescription = dataToSave.customs_description || ''
        if (dataToSave.metal_aluminum !== undefined) {
          itemUpdates.metal_aluminum = dataToSave.metal_aluminum
          itemUpdates.metalAluminum = dataToSave.metal_aluminum
        }
        if (dataToSave.metal_copper !== undefined) {
          itemUpdates.metal_copper = dataToSave.metal_copper
          itemUpdates.metalCopper = dataToSave.metal_copper
        }
        if (dataToSave.metal_steel !== undefined) {
          itemUpdates.metal_steel = dataToSave.metal_steel
          itemUpdates.metalSteel = dataToSave.metal_steel
        }

        // Update the parent component with the SAVED values (from overrides or current state)
        // This ensures we use the values that were actually saved, not stale state
        onUpdate(itemUpdates)
      }
    } catch (error) {
      console.error('Error saving ASIN data:', error)
      alert(error instanceof Error ? error.message : 'Failed to save product defaults')
    } finally {
      setSaving(false)
    }
  }

  const refreshFDAForManualChange = async (nextHTSCode = htsCode, nextDescription = customsDescriptionDraft) => {
    const titleForFDA = nextDescription?.trim() || item.description || ''
    if (!titleForFDA) return

    try {
      setFdaClassifying(true)
      const response = await apiService.autoClassifyFDA(
        titleForFDA,
        nextHTSCode,
        (item as any).htsDescription
      ) as any

      if (!response.success) return

      const nextIsFood = response.isFood === true
      const nextIsCosmetic = response.isCosmetic === true
      const nextFdaCode = nextIsFood ? (response.fdaCode || null) : null
      const nextClassification = response.classification || (nextIsFood ? 'FOOD' : nextIsCosmetic ? 'COSMETIC' : 'NEITHER')

      setIsFood(nextIsFood)
      setIsCosmetic(nextIsCosmetic)
      setFdaProductCode(nextFdaCode || '')

      await saveASINData({
        hts_code: nextHTSCode,
        customs_description: nextDescription || null,
        is_food: nextIsFood,
        is_cosmetic_product: nextIsCosmetic,
        fda_product_code: nextFdaCode,
        fda_classification: nextClassification
      })
    } catch (error) {
      console.error('FDA refresh after manual edit failed:', error)
    } finally {
      setFdaClassifying(false)
    }
  }

  const saveNewManufacturingAddress = async () => {
    try {
      const addressData = isStaff ? { ...newAddress, clientId } : newAddress
      const response = await apiService.saveClientManufacturingAddress(addressData)

      if (response.success) {
        const savedAddress = { ...newAddress, id: response.data.id }

        // Update local addresses list
        setLocalManufacturingAddresses(prev => [...prev, savedAddress])

        // Notify parent component if callback provided
        if (onNewAddressSaved) {
          onNewAddressSaved(savedAddress)
        } else {
          // Fallback: reload client data if no parent callback
          await loadClientASINData()
        }

        // Select the newly created address
        setSelectedAddressId(response.data.id)
        setManufacturingAddress(savedAddress)
        setShowNewAddressForm(false)

        // Reset form
        setNewAddress({
          name: '',
          street_address: '',
          city: '',
          state: '',
          country: '',
          zip_code: '',
          is_food: false,
          fda_registration_id: undefined
        })
      }
    } catch (error) {
      console.error('Error saving manufacturing address:', error)
    }
  }

  const handleAddressSelect = async (addressId: number) => {
    const address = localManufacturingAddresses.find(addr => addr.id === addressId)
    if (address) {
      setSelectedAddressId(addressId)
      setManufacturingAddress(address)

      // Validate country match
      validateCountryMatch(address.country, countryOfOrigin)

      // Update parent component immediately
      onUpdate({
        manufacturingAddress: address
      })

      // Automatically save the selection with both address and ID
      await saveASINData({
        manufacturing_address: address,
        manufacturing_address_id: addressId
      })
    }
  }

  const classifyProductHTS = async () => {
    if (!item.description) return

    const loadingMessages = [
      'Analyzing product details...',
      'Classifying with AI...',
      'Selecting HTS codes...',
      'Almost done...'
    ]

    try {
      setHtsClassifying(true)
      setShowHTSClassification(true)
      setRowExpanded(true)
      setLoadingMessage(loadingMessages[0])

      // Fast message rotation (1s each) - stops immediately when API returns
      let messageIndex = 0
      const messageInterval = setInterval(() => {
        messageIndex = (messageIndex + 1) % loadingMessages.length
        setLoadingMessage(loadingMessages[messageIndex])
      }, 1000) // 1 second per message for faster perceived progress

      const response = await apiService.classifyHTS(item.description, countryOfOrigin, item.asin, item.imageUrl, true)

      // Stop messages immediately when API returns
      clearInterval(messageInterval)

      if (response.success) {
        // Transform AI classification results to match expected format
        let htsCodes = response.htsCodes || [{
          htsCode: response.htsCode,
          description: response.description,
          confidence: response.confidence,
          reasoning: response.reasoning,
          isValid: response.isValid,
          htsValidation: response.htsValidation
        }]

        const transformedResults = {
          htsCodes: htsCodes,
          hasHighConfidence: (response.confidence || 0) >= 85
        }

        setHtsClassificationResults(transformedResults)
        setRowExpanded(true)
        setSelectedHTSOption(null)
      }
    } catch (error) {
      console.error('Error classifying product:', error)
    } finally {
      setHtsClassifying(false)
    }
  }

  const selectHTSCode = async (htsOption: any) => {
    const htsCodeValue = getHTSOptionCode(htsOption)
    if (!normalizeHTSCode(htsCodeValue)) {
      alert('Could not select this HTS option because it is missing a valid HTS code.')
      return false
    }

    flushSync(() => {
      setSelectedHTSOption({ ...htsOption, htsCode: htsCodeValue })
      setHtsCode(htsCodeValue)
      setHtsValidation(null)
      onUpdate({
        htsCode: htsCodeValue,
        htsDescription: htsOption.description || htsOption.fullTitle || htsOption.officialDescription || ''
      } as Partial<ShipmentItem>)
    })
    // Keep classification results visible - don't close the panel

    try {
      // Automatically save the selected HTS code, but do not let a save/FDA refresh
      // hiccup make the user-visible selection disappear.
      await saveASINData({
        hts_code: htsCodeValue,
        hts_description: htsOption.description || htsOption.fullTitle || htsOption.officialDescription || '',
        hts_confidence: htsOption.confidence ?? htsOption.accuracyMatch ?? null
      })
      await refreshFDAForManualChange(htsCodeValue, customsDescriptionDraft)
    } catch (error) {
      console.error('Error after selecting HTS code:', error)
    }
    return true
  }

  const lookupPGAData = async (htsCodeToLookup: string) => {
    if (!htsCodeToLookup || htsCodeToLookup.trim() === '') {
      setPgaData(null)
      return
    }

    try {
      setPgaLoading(true)
      setPgaError(null)

      const response = await apiService.lookupHTSPGA(htsCodeToLookup)

      if (response.success && response.pga) {
        setPgaData(response.pga)
      } else {
        setPgaData({ hasPGA: false, agencies: [], requirementLevel: 'none' })
      }
    } catch (error) {
      console.error('Failed to load PGA data:', error)
      setPgaError('Failed to load PGA information')
      setPgaData({ hasPGA: false, agencies: [], requirementLevel: 'none' })
    } finally {
      setPgaLoading(false)
    }
  }

  // Load PGA data only when details are open; this prevents a request per row during page load.
  useEffect(() => {
    if (rowExpanded && htsCode && htsCode.trim() !== '') {
      lookupPGAData(htsCode)
    } else {
      setPgaData(null)
    }
  }, [rowExpanded, htsCode])


  // Validate HTS code when details are open. Avoid validating every row on initial shipment load.
  useEffect(() => {
    const htsDigits = htsCode.replace(/\D/g, '')
    if (rowExpanded && htsDigits.length === 10) {
      validateHTSCode()
    } else if (!rowExpanded || htsDigits.length !== 10) {
      setHtsValidation(null)
    }
  }, [rowExpanded, htsCode])

  const validateHTSCode = async () => {
    try {
      setHtsValidating(true)
      const response = await apiService.validateHTSCode(htsCode)

      if (response.success && response.data) {
        setHtsValidation(response.data)
      }
    } catch (error) {
      console.error('Error validating HTS code:', error)
      setHtsValidation(null)
    } finally {
      setHtsValidating(false)
    }
  }


  const lookupManufacturingAddressWithAI = async () => {
    if (!item.description) return
    if (!countryOfOrigin) {
      alert('Please select a country of origin before using AI lookup')
      return
    }

    try {
      setAiLookupLoading(true)
      setLoadingMessage('Researching manufacturing addresses...')

      // Add a delay to show the progress message
      setTimeout(() => setLoadingMessage('Analyzing product information...'), 2000)
      setTimeout(() => setLoadingMessage('Finding addresses in ' + (COUNTRIES.find(c => c.code === countryOfOrigin)?.name || countryOfOrigin) + '...'), 4000)

      const response = await apiService.lookupManufacturingAddress(item.description, item.asin, countryOfOrigin)

      if (response.success && response.data) {
        setAiSuggestedAddress({
          name: response.data.name || '',
          street_address: response.data.street_address || '',
          city: response.data.city || '',
          state: response.data.state || '',
          country: response.data.country || countryOfOrigin,
          zip_code: response.data.zip_code || '',
          reasoning: response.data.reasoning || '',
          confidence: response.data.confidence || 0,
          sources: response.data.sources || [],
          research_method: response.data.research_method || 'AI-powered research',
          verification_status: response.data.verification_status || 'researched',
          facility_type: response.data.facility_type || 'manufacturing_facility'
        })
      }
    } catch (error) {
      console.error('Error looking up manufacturing address:', error)
      alert('Failed to lookup manufacturing address. Please try again.')
    } finally {
      setAiLookupLoading(false)
      setLoadingMessage('Searching US customs database...')
    }
  }

  const acceptAISuggestion = async () => {
    if (aiSuggestedAddress) {
      setNewAddress(aiSuggestedAddress)

      // Save the accepted address
      try {
        setSaving(true)
        const addressData = isStaff ? { ...aiSuggestedAddress, clientId } : aiSuggestedAddress
        const response = await apiService.saveClientManufacturingAddress(addressData)

        if (response.success && response.data) {
          const savedAddress = { ...aiSuggestedAddress, id: response.data.id }

          // Update local state with the saved address
          setManufacturingAddress(savedAddress)
          setSelectedAddressId(response.data.id)

          // Re-validate after state is set
          validateCountryMatch(savedAddress.country, countryOfOrigin)

          // Update local addresses list
          setLocalManufacturingAddresses(prev => [...prev, savedAddress])

          // Notify parent component if callback exists
          if (onNewAddressSaved) {
            onNewAddressSaved(savedAddress)
          }

          // Update parent component
          onUpdate({
            manufacturingAddress: savedAddress
          })

          // Save to ASIN data for future preloading
          await saveASINData({
            manufacturing_address: savedAddress,
            manufacturing_address_id: savedAddress.id
          })

          // Clear the suggestion and close form
          setAiSuggestedAddress(null)
          setShowNewAddressForm(false)
          setNewAddress({
            name: '',
            street_address: '',
            city: '',
            state: '',
            country: '',
            zip_code: '',
            is_food: false,
            fda_registration_id: undefined
          })
        }
      } catch (error) {
        console.error('Error saving accepted address:', error)
        alert('Failed to save the address. Please try again.')
      } finally {
        setSaving(false)
      }
    }
  }

  const rejectAISuggestion = () => {
    setAiSuggestedAddress(null)
  }

  // AI auto-find: lookup manufacturer and auto-save in one step
  const autoFindManufacturer = async () => {
    if (!item.description) return
    if (!countryOfOrigin) {
      alert('Please select a country of origin first')
      return
    }

    try {
      setAiLookupLoading(true)

      const response = await apiService.lookupManufacturingAddress(item.description, item.asin, countryOfOrigin)

      if (response.success && response.data) {
        const candidateText = [
          response.data.name,
          response.data.street_address,
          response.data.reasoning,
          response.data.verification_status,
          response.data.facility_type
        ].filter(Boolean).join(' ').toLowerCase()
        const forbiddenCandidate = /manual[ -]?verification|manual[ -]?review|required verification|unconfirmed|placeholder|pending verification|factory address pending|staff[ -]?review/.test(candidateText)
        const completeCandidate = Boolean(
          response.data.found !== false &&
          response.data.name &&
          response.data.street_address &&
          response.data.city &&
          response.data.country &&
          response.data.zip_code
        )
        const verifiedCandidate = response.data.verification_status === 'verified' &&
          response.data.auto_selectable === true
        if (forbiddenCandidate || !completeCandidate || !verifiedCandidate || Number(response.data.confidence || 0) < 3) {
          alert('No verified manufacturer address was found. Nothing was auto-selected.')
          return
        }

        const addr = {
          name: response.data.name || '',
          street_address: response.data.street_address || '',
          city: response.data.city || '',
          state: response.data.state || '',
          country: response.data.country || countryOfOrigin,
          zip_code: response.data.zip_code || '',
          reasoning: response.data.reasoning || '',
          confidence: response.data.confidence || 0,
          sources: response.data.sources || [],
          research_method: response.data.research_method || 'AI-powered research',
          verification_status: response.data.verification_status || 'researched',
          facility_type: response.data.facility_type || 'manufacturing_facility'
        }

        // Auto-save the address
        setSaving(true)
        const addressData = isStaff ? { ...addr, clientId } : addr
        const saveResponse = await apiService.saveClientManufacturingAddress(addressData)

        if (saveResponse.success && saveResponse.data) {
          const savedAddress = { ...addr, id: saveResponse.data.id }
          setManufacturingAddress(savedAddress)
          setSelectedAddressId(saveResponse.data.id)
          validateCountryMatch(savedAddress.country, countryOfOrigin)
          setLocalManufacturingAddresses(prev => [...prev, savedAddress])
          if (onNewAddressSaved) { onNewAddressSaved(savedAddress) }
          onUpdate({ manufacturingAddress: savedAddress })
          await saveASINData({ manufacturing_address: savedAddress, manufacturing_address_id: savedAddress.id })
        }
        setSaving(false)
      } else {
        alert('AI could not find a manufacturer for this product. Try expanding the row to add manually.')
      }
    } catch (error) {
      console.error('Error auto-finding manufacturer:', error)
      alert('Failed to find manufacturer. Please try again.')
      setSaving(false)
    } finally {
      setAiLookupLoading(false)
    }
  }

  const handleCheckSSBRate = async () => {
    if (!htsCode || !countryOfOrigin || (item.declaredValue === undefined || item.declaredValue === null)) {
      alert('HTS Code, Country of Origin, and COGS are required for SSB rate check')
      return
    }

    // Must be 10 digits
    if (htsCode.replace(/\D/g, '').length !== 10) {
      alert('A valid 10-digit HTS code is required for SSB rate check')
      return
    }

    try {
      setSsbRateLoading(true)

      const declaredValue = item.declaredValue || 0

      // Safety check: Ensure we have a valid positive number
      if (isNaN(declaredValue) || declaredValue <= 0) {
        console.error('SSB Rate Check: Invalid price', { declaredValue })
        alert('Invalid item price for duty calculation. Please check COGS.')
        return
      }

      console.log('🚀 Checking SSB rate (CAD)...', {
        htsCode,
        countryOfOrigin,
        priceCAD: declaredValue
      })

      const response = await apiService.checkSSBRate({
        htsCode,
        countryOfOrigin,
        price: declaredValue, // Send CAD price
        description: item.description,
        asin: item.asin,
        manufacturingAddress: manufacturingAddress?.street_address,
        metal_aluminum: metalAluminum,
        metal_copper: metalCopper,
        metal_steel: metalSteel
      })

      if (response.success && response.data) {
        // Backend returns CAD/unit for shipment rows after deriving the rate from SSB.
        setSsbVerifiedData(response.data)
        onSSBRateCalculated?.(response.data)
        onDutyCalculated?.(response.data.dutyAmount)
      } else {
        alert('Failed to check SSB rate: ' + (response.error || 'Unknown error'))
      }
    } catch (error) {
      console.error('Error checking SSB rate:', error)
      alert('Failed to check SSB rate. Please try again.')
    } finally {
      setSsbRateLoading(false)
    }
  }

  const validateCountryMatch = (addressCountry: string, originCountry: string) => {
    if (addressCountry && originCountry && addressCountry !== originCountry) {
      const addressCountryName = COUNTRIES.find(c => c.code === addressCountry)?.name || addressCountry
      const originCountryName = COUNTRIES.find(c => c.code === originCountry)?.name || originCountry

      setCountryMismatchError(
        `Critical Error: Manufacturing address is in ${addressCountryName} but Country of Origin is set to ${originCountryName}. These must match for customs compliance.`
      )
      return false
    } else {
      setCountryMismatchError(null)
      return true
    }
  }

  // Helper: get selected manufacturing address for food reg ID display
  const selectedAddr = localManufacturingAddresses.find(a => a.id === selectedAddressId)

  return (
    <>
      {/* Main Table Row */}
      <div
        className="shipment-item-row"
        style={{
          display: 'grid',
          gridTemplateColumns: SHIPMENT_TABLE_GRID,
          alignItems: 'center',
          borderBottom: rowExpanded ? 'none' : '1px solid #e9ecef',
          padding: '6px 0',
          backgroundColor: validationErrors.length > 0 ? '#fff5f5' : '#fff',
          fontSize: '13px',
          minHeight: '48px'
        }}
      >
        {/* Image */}
        <div style={{ padding: '0 6px' }}>
          {item.imageUrl ? (
            <img src={item.imageUrl} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 3, border: '1px solid #ddd' }} />
          ) : (
            <div style={{ width: 40, height: 40, backgroundColor: '#e9ecef', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', color: '#999' }}>N/A</div>
          )}
        </div>

        {/* Title + ASIN */}
        <div style={{ padding: '0 6px', overflow: 'hidden' }}>
          <div style={{ fontWeight: 600, fontSize: '12px', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' } as any} title={item.description}>
            {item.description}
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 1 }}>
            {item.asin && <span style={{ fontSize: '10px', color: '#666', fontFamily: 'monospace' }}>{item.asin}</span>}
            {item.sku && <span style={{ fontSize: '10px', color: '#999' }}>SKU: {item.sku}</span>}
            {(confirmedASINHistory.length > 0 || hasSavedASINRecommendation(savedASINRecommendation)) && (
              <span style={{ fontSize: '9px', color: '#0f5132', backgroundColor: '#d1e7dd', border: '1px solid #badbcc', borderRadius: 3, padding: '1px 4px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                {confirmedASINHistory.length > 0 ? `${confirmedASINHistory.length} confirmed` : 'Saved default'}
              </span>
            )}
            {lastConfirmedManualOption && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  if (confirmedManualOptions.length > 1) {
                    setManualHistoryOpen(true)
                    setRowExpanded(true)
                  } else {
                    applyLastConfirmedManualOption()
                  }
                }}
                title={confirmedManualOptions.length > 1
                  ? `Show ${confirmedManualOptions.length} confirmed previous manual options`
                  : `Apply confirmed previous manual ${lastConfirmedManualOption.fields} from shipment ${lastConfirmedManualOption.entry.shipment_id}`}
                style={{
                  fontSize: '9px',
                  color: '#475569',
                  backgroundColor: '#f8fafc',
                  border: '1px solid #cbd5e1',
                  borderRadius: 3,
                  padding: '1px 4px',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  cursor: 'pointer'
                }}
              >
                Last manual{confirmedManualOptions.length > 1 ? ` ${confirmedManualOptions.length} v` : ''}
              </button>
            )}
          </div>
        </div>

        {/* Qty */}
        <div style={{ padding: '0 4px', textAlign: 'center', fontWeight: 600 }}>{item.quantity}</div>

        {/* Country */}
        <div style={{ padding: '0 4px' }}>
          <select
            value={countryOfOrigin}
            onChange={async (e) => {
              const newValue = e.target.value
              setCountryOfOrigin(newValue)
              if (isCusmaCountry(newValue)) { setCusmaApplicable(true) } else { setCusmaApplicable(false) }
              if (manufacturingAddress) { validateCountryMatch(manufacturingAddress.country, newValue) }
              onUpdate({ countryOfOrigin: newValue })
              await saveASINData({ country_of_origin: newValue })
            }}
            disabled={saving}
            style={{ width: '100%', padding: '4px 2px', fontSize: '11px', border: `1px solid ${cooManualReview ? '#f97316' : (!countryOfOrigin || countryMismatchError ? '#dc2626' : '#ddd')}`, borderRadius: 3, backgroundColor: cooManualReview ? '#fff7ed' : (!countryOfOrigin || countryMismatchError ? '#fff5f5' : '#fff') }}
          >
            <option value="">Select...</option>
            {COUNTRIES.map(c => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
          {countrySuggestion && countrySuggestion.countryCode !== countryOfOrigin && (
            <div style={{ marginTop: 3, padding: '3px 5px', backgroundColor: '#e3f2fd', border: '1px solid #2196f3', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
              <span style={{ fontSize: '9px', color: '#1565c0', fontWeight: 600, lineHeight: 1.3 }}>
                YYZ: {COUNTRIES.find(c => c.code === countrySuggestion.countryCode)?.name || countrySuggestion.countryCode}
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); applyCountrySuggestion() }}
                style={{ flexShrink: 0, padding: '1px 5px', fontSize: '9px', fontWeight: 700, backgroundColor: '#2196f3', color: '#fff', border: 'none', borderRadius: 2, cursor: 'pointer' }}
              >
                Apply
              </button>
            </div>
          )}
          {cooManualReview && yyzRecommendedCOO && (
            <div style={{ marginTop: 3, padding: '3px 5px', backgroundColor: '#ffedd5', border: '1px solid #fb923c', borderRadius: 3, fontSize: '9px', color: '#9a3412', fontWeight: 700, lineHeight: 1.3 }}>
              Manual review: current {countryOfOrigin || 'blank'}, YYZ {getCountryName(yyzRecommendedCOO)}
            </div>
          )}
          {cooAutoFilled && yyzRecommendedCOO && (
            <div style={{ marginTop: 3, padding: '3px 5px', backgroundColor: '#dcfce7', border: '1px solid #86efac', borderRadius: 3, fontSize: '9px', color: '#166534', fontWeight: 700, lineHeight: 1.3 }}>
              Filled from YYZ: {getCountryName(yyzRecommendedCOO)}
            </div>
          )}
        </div>

        {/* HTS */}
        <div ref={htsDropdownRef} style={{ padding: '0 4px', position: 'relative' }}>
          <div style={{ display: 'flex', gap: 2 }}>
            <input
              type="text"
              value={htsCode}
              onChange={(e) => {
                const newValue = e.target.value.replace(/[^\d.]/g, '')
                setHtsCode(newValue)
                setHtsValidation(null)
                onUpdate({ htsCode: newValue })
              }}
              onBlur={async () => {
                await saveASINData({ hts_code: htsCode })
                if (htsCode.replace(/\D/g, '').length === 10) {
                  await refreshFDAForManualChange(htsCode, customsDescriptionDraft)
                }
              }}
              disabled={saving || snapshotLocked}
              placeholder="0000.00.0000"
              autoComplete="one-time-code"
              style={{ flex: 1, minWidth: 0, padding: '4px', fontSize: '11px', fontFamily: 'monospace', border: `1px solid ${!htsCode || htsCode.replace(/\D/g, '').length !== 10 ? '#dc2626' : '#ddd'}`, borderRadius: 3, backgroundColor: (!htsCode || htsCode.replace(/\D/g, '').length !== 10) ? '#fff5f5' : '#fff' }}
            />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                // Always do a fresh AI classification - clear old results first
                setHtsClassificationResults(null)
                setSelectedHTSOption(null)
                classifyProductHTS()
              }}
              disabled={htsClassifying || snapshotLocked || !item.description}
              style={{ padding: '2px 5px', fontSize: '10px', fontWeight: 600, backgroundColor: htsClassifying ? '#6c757d' : '#198754', color: '#fff', border: 'none', borderRadius: 3, cursor: (htsClassifying || !item.description) ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}
              title="Auto Classify HTS Code with AI"
            >
              {htsClassifying ? '...' : 'AI'}
            </button>
          </div>
          {item.outboundSnapshot && (
            <div style={{ marginTop: 3, fontSize: '9px', lineHeight: 1.25 }}>
              <button
                type="button"
                onClick={toggleHTSHistory}
                style={{ padding: 0, border: 0, background: 'transparent', color: item.outboundSnapshot.status === 'locked' ? '#7c2d12' : '#1d4ed8', cursor: 'pointer', fontSize: '9px', fontWeight: 700 }}
                title="Show the HTS change audit trail"
              >
                {item.outboundSnapshot.status === 'locked'
                  ? `Submitted snapshot${item.outboundSnapshot.ssbShortId ? ` ${item.outboundSnapshot.ssbShortId}` : ''}`
                  : `Outbound draft v${item.outboundSnapshot.revision}`} · History
              </button>
              {item.outboundSnapshot.status === 'locked' && (
                <div style={{ color: '#7c2d12' }}>This outbound retains the HTS originally submitted.</div>
              )}
              {htsHistoryOpen && (
                <div style={{ position: 'absolute', zIndex: 30, top: '100%', left: 4, width: 360, maxHeight: 240, overflow: 'auto', padding: 8, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 4, boxShadow: '0 6px 18px rgba(0,0,0,.18)', color: '#334155', fontSize: '10px' }}>
                  {htsHistoryLoading ? 'Loading history…' : htsEvents.length === 0 ? 'No recorded HTS changes yet.' : htsEvents.map(event => (
                    <div key={event.event_uuid} style={{ padding: '5px 0', borderBottom: '1px solid #e2e8f0' }}>
                      <strong>{event.old_hts_code || 'blank'} → {event.new_hts_code || 'blank'}</strong><br />
                      {event.source} · {event.actor_email || event.actor_role || 'system'} · {new Date(event.occurred_at).toLocaleString()}
                      {event.outbound_id ? <><br />Outbound {event.outbound_id}</> : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {metalCompositionRequired && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 5, padding: '5px', backgroundColor: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                <div style={{ fontSize: '10px', fontWeight: 800, color: '#92400e', lineHeight: 1.2 }}>
                  Metal % for SSB
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    suggestMetalComposition()
                  }}
                  disabled={saving || metalAiLoading || !item.description || htsCode.replace(/\D/g, '').length !== 10}
                  title="AI suggest metal percentages"
                  style={{ padding: '2px 6px', fontSize: '9px', fontWeight: 800, backgroundColor: metalAiLoading ? '#6c757d' : '#7c3aed', color: '#fff', border: 'none', borderRadius: 3, cursor: (saving || metalAiLoading || !item.description || htsCode.replace(/\D/g, '').length !== 10) ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', lineHeight: 1.2 }}
                >
                  {metalAiLoading ? '...' : 'AI'}
                </button>
              </div>
              {requiredMetalFields.map((field) => {
                const label = field === 'metal_aluminum' ? 'Aluminum %' : field === 'metal_copper' ? 'Copper %' : 'Steel %'
                const shortLabel = field === 'metal_aluminum' ? 'Aluminum' : field === 'metal_copper' ? 'Copper' : 'Steel'
                const numericValue = parseMetalInput(getMetalFieldValue(field))
                return (
                  <div key={field} style={{ display: 'grid', gridTemplateColumns: '58px minmax(48px, 1fr) 12px', alignItems: 'center', gap: 4 }}>
                    <label style={{ fontSize: '10px', fontWeight: 700, color: '#78350f', lineHeight: 1.1 }}>
                      {shortLabel}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={getMetalFieldValue(field)}
                      onChange={(e) => {
                        const value = e.target.value
                        setMetalFieldValue(field, value)
                        const nextValue = parseMetalInput(value)
                        onUpdate({
                          [field]: nextValue,
                          ...(field === 'metal_aluminum' ? { metalAluminum: nextValue } : {}),
                          ...(field === 'metal_copper' ? { metalCopper: nextValue } : {}),
                          ...(field === 'metal_steel' ? { metalSteel: nextValue } : {})
                        } as Partial<ShipmentItem>)
                      }}
                      onBlur={() => saveMetalComposition([field])}
                      disabled={saving || metalAiLoading}
                      placeholder="0"
                      aria-label={label}
                      title={label}
                      style={{ width: '100%', minWidth: 0, padding: '3px 4px', fontSize: '11px', border: `1px solid ${numericValue === null ? '#dc2626' : '#d97706'}`, borderRadius: 3, backgroundColor: '#fff', color: '#111827' }}
                    />
                    <span style={{ fontSize: '10px', fontWeight: 800, color: '#92400e' }}>%</span>
                  </div>
                )
              })}
            </div>
          )}
          {/* HTS Classification Dropdown */}
          {showHTSClassification && htsClassificationResults && !htsClassifying && !rowExpanded && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: -260,
              zIndex: 1000,
              backgroundColor: '#fff',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              boxShadow: '0 12px 28px rgba(15,23,42,0.18)',
              marginTop: 2,
              width: 680,
              maxWidth: 'calc(100vw - 48px)'
            }}>
              <div style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', fontSize: '12px', fontWeight: 700, color: '#374151', backgroundColor: '#f9fafb', position: 'sticky', top: 0, zIndex: 1 }}>
                HTS Classification Results ({htsClassificationResults.htsCodes.filter((o: any) => o.applicability !== 'not_applicable').length})
                <button type="button" onClick={() => setShowHTSClassification(false)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#999', lineHeight: 1 }}>&times;</button>
              </div>
              {htsClassificationResults.htsCodes
                .filter((option: any) => option.applicability !== 'not_applicable')
                .slice(0, 8)
                .map((option: any, index: number) => {
                  const optionCode = getHTSOptionCode(option)
                  const isSelected = normalizeHTSCode(htsCode).length === 10 && normalizeHTSCode(optionCode) === normalizeHTSCode(htsCode)
                  return (
                    <div
                      key={optionCode || index}
                      onMouseDown={async (e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        const selected = await selectHTSCode(option)
                        if (selected) setShowHTSClassification(false)
                      }}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f3f4f6',
                        backgroundColor: isSelected ? '#eff6ff' : '#fff',
                        transition: 'background-color 0.1s'
                      }}
                      onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb' }}
                      onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#fff' }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 4 }}>
                        <span style={{ fontSize: '13px', fontWeight: 700, fontFamily: 'monospace', color: '#0066cc', whiteSpace: 'nowrap' }}>
                          {optionCode}
                        </span>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          <span style={{ fontSize: '9px', fontWeight: 700, padding: '1px 4px', borderRadius: 2, backgroundColor: (option.confidence || option.accuracyMatch) >= 85 ? '#dcfce7' : '#fef9c3', color: (option.confidence || option.accuracyMatch) >= 85 ? '#15803d' : '#a16207' }}>
                            {option.confidence || option.accuracyMatch}%
                          </span>
                          {isSelected && <span style={{ fontSize: '9px', fontWeight: 700, padding: '1px 4px', borderRadius: 2, backgroundColor: '#dbeafe', color: '#1e40af' }}>SELECTED</span>}
                        </div>
                      </div>
                      {option.fullTitle && (
                        <div style={{ fontSize: '11px', color: '#4b5563', lineHeight: 1.35, whiteSpace: 'normal' }}>
                          {option.fullTitle}
                        </div>
                      )}
                      {option.dutyRate && (
                        <div style={{ fontSize: '10px', color: option.dutyRate === 'Free' || option.dutyRate === '0.0%' ? '#15803d' : '#991b1b', fontWeight: 500, marginTop: 2 }}>
                          Duty: {option.dutyRate}{option.dutyAmount !== undefined ? ` ($${option.dutyAmount.toFixed(2)}/unit)` : ''}
                        </div>
                      )}
                    </div>
                  )
                })}
              <div style={{ padding: '6px 10px', borderTop: '1px solid #e5e7eb', fontSize: '10px', color: '#9ca3af', textAlign: 'center' }}>
                Click to select &middot; <span style={{ cursor: 'pointer', color: '#0066cc', textDecoration: 'underline' }} onClick={() => { setRowExpanded(true); }}>View full details</span>
              </div>
            </div>
          )}
        </div>

        {/* COGS */}
        <div style={{ padding: '0 4px' }}>
          <input
            type="number"
            step="0.01"
            min="0"
            value={item.declaredValue ?? ''}
            onChange={(e) => {
              const value = e.target.value
              const newValue = value === '' ? 0 : parseFloat(value) || 0
              onUpdate({ declaredValue: newValue })
            }}
            onBlur={async () => {
              const cogsValue = item.declaredValue || 0
              await saveASINData({ cogs_cad: cogsValue })
            }}
            disabled={saving}
            placeholder="$0.00"
            autoComplete="one-time-code"
            style={{ width: '100%', padding: '4px', fontSize: '12px', border: `1px solid ${!item.declaredValue || item.declaredValue === 0 ? '#dc2626' : '#ddd'}`, borderRadius: 3, backgroundColor: (!item.declaredValue || item.declaredValue === 0) ? '#fff5f5' : '#fff' }}
          />
        </div>

        {/* Duty / SSB */}
        <div style={{ padding: '0 4px', fontSize: '10px' }}>
          {(() => {
            const htsDigits = htsCode.replace(/\D/g, '')
            const hasValidHTS = htsDigits.length === 10
            const hasValidCountry = countryOfOrigin && countryOfOrigin.trim() !== ''
            const hasValidPrice = item.declaredValue && Number(item.declaredValue) > 0

            // SSB approval badge (from htsValidation)
            const ssbBadge = !htsValidating && htsValidation && htsValidation.valid ? (
              <div style={{
                fontSize: '8px',
                fontWeight: 700,
                color: '#fff',
                backgroundColor: htsValidation.authWarning ? '#856404' : htsValidation.is_allowed !== false ? '#28a745' : '#dc3545',
                padding: '1px 4px',
                borderRadius: 2,
                textAlign: 'center',
                marginBottom: 2,
                lineHeight: '12px'
              }}>
                {htsValidation.authWarning ? 'SSB AUTH' : htsValidation.is_allowed !== false ? 'SSB OK' : 'NOT ALLOWED'}
              </div>
            ) : htsValidating ? (
              <div style={{ fontSize: '8px', color: '#6c757d', marginBottom: 2 }}>Validating...</div>
            ) : null

            // SSB verified duty display
            if (ssbVerifiedData) {
              return (
                <div>
                  {ssbBadge}
                  <div style={{ fontWeight: 700, color: '#1565c0', fontSize: '11px' }}>
                    ${ssbVerifiedData.dutyAmount.toFixed(2)}
                  </div>
                  <div style={{ color: '#666', fontSize: '9px' }}>{ssbVerifiedData.dutyRate}</div>
                  <div style={{ color: '#15803d', fontSize: '8px', fontWeight: 600 }}>SSB Verified</div>
                </div>
              )
            }

            // No duty data yet - show check button
            if (hasValidHTS && hasValidCountry && hasValidPrice) {
              return (
                <div>
                  {ssbBadge}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleCheckSSBRate() }}
                    disabled={ssbRateLoading}
                    style={{ padding: '2px 5px', fontSize: '9px', fontWeight: 600, backgroundColor: ssbRateLoading ? '#6c757d' : '#0d6efd', color: '#fff', border: 'none', borderRadius: 2, cursor: ssbRateLoading ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}
                  >
                    {ssbRateLoading ? '...' : 'SSB Check'}
                  </button>
                </div>
              )
            }

            // Missing data
            return (
              <div style={{ color: '#ccc', fontSize: '9px' }}>
                {!hasValidHTS ? 'Need HTS' : !hasValidCountry ? 'Need country' : 'Need COGS'}
              </div>
            )
          })()}
        </div>

        {/* Extended Duty */}
        <div style={{ padding: '0 4px', fontSize: '10px' }}>
          {ssbVerifiedData ? (
            (() => {
              const quantity = Number(item.quantity) || 0
              const dutyPerUnit = Number(ssbVerifiedData.dutyAmount) || 0
              const extendedDuty = dutyPerUnit * quantity
              const isCusmaDutyFree = ssbVerifiedData.source === 'cusma'

              return (
                <div>
                  <div style={{ fontWeight: 700, color: isCusmaDutyFree ? '#15803d' : '#991b1b', fontSize: '11px' }}>
                    ${extendedDuty.toFixed(2)}
                  </div>
                  <div style={{ color: '#666', fontSize: '8px', lineHeight: 1.2 }}>
                    {isCusmaDutyFree ? 'CUSMA duty-free' : `${quantity} x $${dutyPerUnit.toFixed(2)}`}
                  </div>
                </div>
              )
            })()
          ) : (
            <div style={{ color: '#9ca3af', fontSize: '9px' }}>
              Need SSB
            </div>
          )}
        </div>

        {/* Type / FDA */}
        <div style={{ padding: '0 4px' }}>
          <div style={{ display: 'flex', gap: 4, marginBottom: 3 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', fontSize: '9px' }}>
              <input
                type="checkbox"
                checked={isFood}
                onChange={async (e) => {
                  const newIsFood = e.target.checked
                  setIsFood(newIsFood)
                  await saveASINData({ is_food: newIsFood })
                  if (!newIsFood) { setFdaProductCode(''); await saveASINData({ fda_product_code: null }) }
                }}
                style={{ width: 12, height: 12 }}
              />
              Food
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', fontSize: '9px' }}>
              <input
                type="checkbox"
                checked={isCosmetic}
                onChange={async (e) => {
                  const newIsCosmetic = e.target.checked
                  setIsCosmetic(newIsCosmetic)
                  await saveASINData({ is_cosmetic_product: newIsCosmetic })
                }}
                style={{ width: 12, height: 12 }}
              />
              Cosm
            </label>
          </div>
          {(isFood || isCosmetic) && (
            <div style={{ display: 'flex', gap: 2 }}>
              <input
                type="text"
                value={fdaProductCode}
                onChange={(e) => setFdaProductCode(e.target.value)}
                onBlur={async () => { if (fdaProductCode) { await saveASINData({ fda_product_code: fdaProductCode }) } }}
                disabled={saving || fdaClassifying}
                placeholder="FDA Code"
                autoComplete="one-time-code"
                style={{ flex: 1, minWidth: 0, padding: '3px 4px', fontSize: '10px', fontFamily: 'monospace', border: '1px solid #ddd', borderRadius: 3 }}
              />
              <button
                type="button"
                onClick={async (e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (!item.description) { alert('Product description is required for FDA classification'); return }
                  setFdaClassifying(true)
                  setFdaClassificationResult(null)
                  try {
                    const response = await apiService.classifyFDAProductCode(customsDescriptionDraft?.trim() || item.description)
                    if (response.success && response.fdaCode) {
                      setFdaClassificationResult(response)
                      setRowExpanded(true)
                    } else { alert(`FDA Classification failed: ${response.error || 'Unknown error'}`) }
                  } catch (error) {
                    console.error('FDA classification error:', error)
                    alert('Failed to classify FDA product code. Please try again.')
                  } finally { setFdaClassifying(false) }
                }}
                disabled={saving || fdaClassifying}
                style={{ padding: '2px 5px', fontSize: '10px', fontWeight: 600, backgroundColor: (saving || fdaClassifying) ? '#ccc' : '#0d6efd', color: '#fff', border: 'none', borderRadius: 3, cursor: (saving || fdaClassifying) ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}
              >
                {fdaClassifying ? '...' : 'AI'}
              </button>
            </div>
          )}
        </div>

        {/* Custom Description */}
        <div style={{ padding: '0 4px' }}>
          <input
            type="text"
            value={customsDescriptionDraft}
            onChange={(e) => { setCustomsDescriptionDraft(e.target.value) }}
            onBlur={async () => {
              onUpdate({ customsDescription: customsDescriptionDraft })
              await saveASINData({ customs_description: customsDescriptionDraft || null })
              if (htsCode.replace(/\D/g, '').length === 10) {
                await refreshFDAForManualChange(htsCode, customsDescriptionDraft)
              }
            }}
            placeholder="Override..."
            autoComplete="one-time-code"
            style={{ width: '100%', padding: '4px', fontSize: '11px', border: customsDescriptionDraft ? '2px solid #f59e0b' : '1px solid #ddd', borderRadius: 3 }}
          />
        </div>

        {/* Manufacturing Address */}
        <div style={{ padding: '0 4px' }}>
          <div style={{ display: 'flex', gap: 2 }}>
            <select
              value={selectedAddressId || ''}
              onChange={async (e) => {
                if (e.target.value) {
                  handleAddressSelect(parseInt(e.target.value))
                } else {
                  setSelectedAddressId(null)
                  setManufacturingAddress(undefined)
                  setCountryMismatchError(null)
                  onUpdate({ manufacturingAddress: undefined })
                  await saveASINData({ manufacturing_address: undefined, manufacturing_address_id: null })
                }
              }}
              style={{ flex: 1, minWidth: 0, padding: '4px 2px', fontSize: '11px', border: `1px solid ${countryMismatchError ? '#dc2626' : '#ddd'}`, borderRadius: 3, backgroundColor: countryMismatchError ? '#fff5f5' : '#fff' }}
            >
              <option value="">Select...</option>
              {localManufacturingAddresses.map(addr => (
                <option key={addr.id} value={addr.id}>{addr.name} - {addr.city}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                autoFindManufacturer()
              }}
              disabled={aiLookupLoading || !item.description || !countryOfOrigin}
              style={{ padding: '2px 5px', fontSize: '10px', fontWeight: 600, backgroundColor: aiLookupLoading ? '#6c757d' : '#0ea5e9', color: '#fff', border: 'none', borderRadius: 3, cursor: (aiLookupLoading || !item.description || !countryOfOrigin) ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}
              title="AI auto-find manufacturer"
            >
              {aiLookupLoading ? '...' : 'AI'}
            </button>
          </div>
        </div>

        {/* Food Registration ID */}
        <div style={{ padding: '0 4px', fontSize: '10px', fontFamily: 'monospace', color: selectedAddr?.fda_registration_id ? '#333' : '#ccc' }}>
          {selectedAddr?.fda_registration_id || '-'}
        </div>

        {/* Expand/Collapse */}
        <div style={{ padding: '0 4px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
          {/* Inline warning badges visible without expanding */}
          {!rowExpanded && countryMismatchError && (
            <div
              onClick={(e) => { e.stopPropagation(); setRowExpanded(true) }}
              title={countryMismatchError}
              style={{ fontSize: '9px', fontWeight: 700, backgroundColor: '#dc2626', color: '#fff', padding: '1px 4px', borderRadius: 2, cursor: 'pointer', whiteSpace: 'nowrap', lineHeight: '14px' }}
            >
              ⚠ Country Mismatch
            </div>
          )}

          <button
            onClick={(e) => { e.stopPropagation(); setRowExpanded(prev => !prev) }}
            style={{ background: rowExpanded ? '#e8f0fe' : (countryMismatchError ? '#fff5f5' : 'none'), border: `1px solid ${countryMismatchError && !rowExpanded ? '#dc2626' : '#ccc'}`, borderRadius: 3, cursor: 'pointer', fontSize: '12px', color: '#333', width: 24, height: 24, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'transform 0.2s' }}
            title={rowExpanded ? 'Collapse details' : 'Expand details'}
          >
            <span style={{ display: 'inline-block', transform: rowExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>&#9660;</span>
          </button>
        </div>
      </div>

      {/* Expanded Detail Panel */}
      {rowExpanded && (
        <div style={{ padding: '16px 20px', backgroundColor: '#fafbfc', borderBottom: '2px solid #dee2e6', borderLeft: '3px solid #0d6efd' }}>
          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div style={{ backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', color: '#721c24', padding: '8px 12px', borderRadius: '4px', marginBottom: '12px', fontSize: '13px' }}>
              <strong>Missing required fields:</strong> {validationErrors.join(', ')}
            </div>
          )}

          {/* Product Summary */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <strong style={{ fontSize: '14px' }}>{item.description}</strong>
            {item.asin && <span style={{ fontSize: '12px', color: '#666' }}>ASIN: {item.asin}</span>}
            {item.fnsku && <span style={{ fontSize: '12px', color: '#666' }}>FNSKU: {item.fnsku}</span>}
            {item.declaredValue && item.declaredValue > 0 && exchangeRate && (
              <span style={{ fontSize: '12px', padding: '2px 6px', backgroundColor: '#e3f2fd', borderRadius: '3px' }}>
                Total: ${(item.quantity * Number(item.declaredValue)).toFixed(2)} CAD (${(item.quantity * Number(item.declaredValue) * exchangeRate.cadToUsd).toFixed(2)} USD)
              </span>
            )}
            {item.expiryDate && <span style={{ fontSize: '12px', color: '#666' }}>Expires: {new Date(item.expiryDate).toLocaleDateString()}</span>}
          </div>

          {/* CUSMA Checkbox */}
          {isCusmaCountry(countryOfOrigin) && (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '6px 12px', backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '4px', marginBottom: '12px', fontSize: '13px' }}>
              <input
                type="checkbox"
                checked={cusmaApplicable}
                onChange={(e) => {
                  const checked = e.target.checked
                  setCusmaApplicable(checked)
                  onUpdate({ cusmaApplicable: checked, is_usmca_certified: checked })
                }}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontWeight: '600', color: '#166534' }}>CUSMA/USMCA Applicable (Duty-Free for US/CA/MX products)</span>
            </label>
          )}

          {lastConfirmedManualOption && (
            <div style={{ marginBottom: '12px', padding: '8px 10px', backgroundColor: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '12px', color: '#334155' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                <div>
                  <strong style={{ color: '#475569' }}>Staff hint: confirmed manual {lastConfirmedManualOption.fields}</strong>
                  <span style={{ color: '#64748b', marginLeft: '6px' }}>
                    {confirmedManualOptions.length > 1
                      ? `${confirmedManualOptions.length} title/HTS option${confirmedManualOptions.length === 1 ? '' : 's'} from confirmed SSB history`
                      : `from shipment ${lastConfirmedManualOption.entry.shipment_id}${lastConfirmedManualOption.entry.ssb_short_id ? ` / ${lastConfirmedManualOption.entry.ssb_short_id}` : ''}`}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  {confirmedManualOptions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setManualHistoryOpen(prev => !prev)}
                      style={{ padding: '4px 8px', border: '1px solid #cbd5e1', backgroundColor: '#fff', color: '#475569', borderRadius: '3px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      {manualHistoryOpen ? 'Hide options ^' : 'Show options v'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={applyLastConfirmedManualOption}
                    style={{ padding: '4px 8px', border: '1px solid #94a3b8', backgroundColor: '#fff', color: '#334155', borderRadius: '3px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    Apply Latest
                  </button>
                </div>
              </div>
              <div style={{ marginTop: '6px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '4px 10px' }}>
                {lastConfirmedManualOption.description && <div><strong>Title:</strong> {lastConfirmedManualOption.description}</div>}
                {lastConfirmedManualOption.htsCode && <div><strong>HTS:</strong> <span style={{ fontFamily: 'monospace' }}>{lastConfirmedManualOption.htsCode}</span>{lastConfirmedManualOption.htsDescription ? ` - ${lastConfirmedManualOption.htsDescription}` : ''}</div>}
              </div>
              {confirmedManualOptions.length > 1 && manualHistoryOpen && (
                <div style={{ marginTop: '8px', display: 'grid', gap: '6px' }}>
                  {confirmedManualOptions.map((option, index) => (
                    <div key={option.key} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: '8px', alignItems: 'center', padding: '7px 8px', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '3px', color: '#64748b' }}>
                          <strong style={{ color: '#475569' }}>Option {index + 1}</strong>
                          <span>Shipment {option.entry.shipment_id}</span>
                          {option.entry.ssb_short_id && <span>SSB {option.entry.ssb_short_id}</span>}
                          <span>{formatHistoryDate(option.entry.submitted_at)}</span>
                          {option.occurrences > 1 && <span>{option.occurrences} uses</span>}
                        </div>
                        {option.description && (
                          <div style={{ overflowWrap: 'anywhere' }}><strong>Title:</strong> {option.description}</div>
                        )}
                        {option.htsCode && (
                          <div style={{ overflowWrap: 'anywhere' }}><strong>HTS:</strong> <span style={{ fontFamily: 'monospace' }}>{option.htsCode}</span>{option.htsDescription ? ` - ${option.htsDescription}` : ''}</div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => applyConfirmedManualOption(option)}
                        style={{ padding: '4px 8px', border: '1px solid #94a3b8', backgroundColor: '#fff', color: '#334155', borderRadius: '3px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >
                        Apply
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Confirmed shipment history */}
          {confirmedASINHistory.length > 0 && (
            <div style={{ marginBottom: '12px', padding: '10px 12px', backgroundColor: '#eff6ff', border: '1px solid #93c5fd', borderRadius: '4px', fontSize: '12px' }}>
              <div style={{ marginBottom: '8px' }}>
                <strong style={{ color: '#1d4ed8' }}>Confirmed previous SSB submissions</strong>
                <span style={{ color: '#1e40af', marginLeft: '6px' }}>These were recorded only after SSB submission succeeded.</span>
              </div>
              <div style={{ display: 'grid', gap: '8px' }}>
                {confirmedASINHistory.map((entry, index) => (
                  <div key={`${entry.shipment_id}-${entry.ssb_uuid || index}-${entry.submitted_at || index}`} style={{ padding: '8px', backgroundColor: '#fff', border: '1px solid #bfdbfe', borderRadius: '4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginBottom: '4px' }}>
                      <strong style={{ color: '#1e3a8a' }}>Shipment {entry.shipment_id}</strong>
                      <span style={{ color: '#64748b' }}>{formatHistoryDate(entry.submitted_at)}</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '4px 10px', color: '#1e3a8a' }}>
                      {entry.country_of_origin && <div><strong>COO:</strong> {getCountryName(entry.country_of_origin)}</div>}
                      {entry.hts_code && <div><strong>HTS:</strong> <span style={{ fontFamily: 'monospace' }}>{entry.hts_code}</span></div>}
                      {entry.fda_classification && <div><strong>FDA:</strong> {entry.fda_classification}{entry.fda_product_code ? ` / ${entry.fda_product_code}` : ''}</div>}
                      {(entry.is_food || entry.is_cosmetic_product) && <div><strong>Type:</strong> {entry.is_food ? 'Food' : ''}{entry.is_food && entry.is_cosmetic_product ? ' / ' : ''}{entry.is_cosmetic_product ? 'Cosmetic' : ''}</div>}
                      {entry.product_quantity && <div><strong>Qty:</strong> {entry.product_quantity}</div>}
                      {entry.product_value_usd && <div><strong>Value:</strong> ${Number(entry.product_value_usd).toFixed(2)} USD</div>}
                      {entry.ssb_short_id && <div><strong>SSB:</strong> {entry.ssb_short_id}</div>}
                      {entry.customs_description && <div style={{ gridColumn: '1 / -1' }}><strong>Title:</strong> {entry.customs_description}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {yyzASINOccurrences.length > 0 && (
            <div style={{ marginBottom: '12px', padding: '8px 12px', backgroundColor: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '12px', color: '#334155' }}>
              <strong>YYZ shipment appearances:</strong> {yyzASINOccurrences.length}
              <span style={{ color: '#64748b', marginLeft: '6px' }}>These prove the SKU/ASIN appeared in YYZ shipments, but they are not confirmed classification snapshots unless listed above.</span>
              <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {yyzASINOccurrences.map((entry, index) => (
                  <span key={`${entry.shipment_id}-${index}`} style={{ padding: '2px 6px', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '3px', fontFamily: 'monospace' }}>
                    {entry.shipment_id}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Saved draft/default recommendation */}
          {hasSavedASINRecommendation(savedASINRecommendation) && savedASINRecommendation && (
            <div style={{ marginBottom: '12px', padding: '10px 12px', backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '4px', fontSize: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
                <div>
                  <strong style={{ color: '#166534' }}>Saved SKU/ASIN default</strong>
                  <span style={{ color: '#3f6212', marginLeft: '6px' }}>Editable memory. This may include test edits until a confirmed SSB submission is recorded above.</span>
                </div>
                <button
                  type="button"
                  onClick={applySavedASINRecommendation}
                  style={{ padding: '4px 10px', border: 'none', backgroundColor: '#16a34a', color: '#fff', borderRadius: '3px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  Apply Saved Default
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '6px 10px', color: '#14532d' }}>
                {savedASINRecommendation.country_of_origin && (
                  <div><strong>COO:</strong> {getCountryName(savedASINRecommendation.country_of_origin)}</div>
                )}
                {savedASINRecommendation.hts_code && (
                  <div><strong>HTS:</strong> <span style={{ fontFamily: 'monospace' }}>{savedASINRecommendation.hts_code}</span></div>
                )}
                {savedASINRecommendation.customs_description && (
                  <div style={{ gridColumn: 'span 2' }}><strong>Title:</strong> {savedASINRecommendation.customs_description}</div>
                )}
                {getFDAHistoryLabel(savedASINRecommendation) && (
                  <div><strong>FDA:</strong> {getFDAHistoryLabel(savedASINRecommendation)}</div>
                )}
                {getMetalHistoryLabel(savedASINRecommendation) && (
                  <div><strong>Metal:</strong> {getMetalHistoryLabel(savedASINRecommendation)}</div>
                )}
                {savedASINRecommendation.cogs_cad !== null && savedASINRecommendation.cogs_cad !== undefined && (
                  <div><strong>COGS:</strong> ${Number(savedASINRecommendation.cogs_cad).toFixed(2)} CAD</div>
                )}
                {getManufacturingAddressLabel(savedASINRecommendation.manufacturing_address) && (
                  <div style={{ gridColumn: 'span 2' }}><strong>Mfr:</strong> {getManufacturingAddressLabel(savedASINRecommendation.manufacturing_address)}</div>
                )}
              </div>
            </div>
          )}

          {/* Country Suggestion */}
          {countrySuggestion && countrySuggestion.countryCode !== countryOfOrigin && (
            <div style={{ marginBottom: '12px', padding: '8px 12px', backgroundColor: '#e3f2fd', border: '1px solid #2196f3', borderRadius: '4px', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ color: '#1976d2' }}>
                <strong>YYZ Data Suggestion:</strong> Verified country of origin for this ASIN: {COUNTRIES.find(c => c.code === countrySuggestion.countryCode)?.name || countrySuggestion.countryCode}
              </span>
              <button onClick={applyCountrySuggestion} style={{ padding: '2px 8px', border: 'none', backgroundColor: '#2196f3', color: '#fff', borderRadius: '3px', fontSize: '11px', cursor: 'pointer' }}>Apply</button>
            </div>
          )}

          {/* Country Mismatch Error */}
          {countryMismatchError && (
            <div style={{ padding: '10px 12px', backgroundColor: '#f8d7da', border: '2px solid #dc2626', borderRadius: '4px', color: '#721c24', fontSize: '13px', fontWeight: 'bold', marginBottom: '12px' }}>
              {countryMismatchError}
            </div>
          )}

          {/* HTS Validation + Auto Classify + Duty section */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            {/* Left: HTS Details */}
            <div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>HTS Classification</div>
              <button
                onClick={classifyProductHTS}
                disabled={htsClassifying || !item.description}
                style={{ padding: '6px 12px', border: '1px solid #198754', backgroundColor: htsClassifying ? '#6c757d' : '#198754', color: '#fff', borderRadius: '4px', fontSize: '12px', whiteSpace: 'nowrap', marginBottom: '8px', cursor: (htsClassifying || !item.description) ? 'not-allowed' : 'pointer' }}
              >
                {htsClassifying ? 'Searching...' : 'Auto Classify HTS'}
              </button>

              {/* HTS Validation Message */}
              {htsCode && htsCode.replace(/\D/g, '').length !== 10 && (
                <div style={{ padding: '4px 8px', backgroundColor: '#fee2e2', border: '1px solid #dc2626', borderRadius: '4px', fontSize: '11px', color: '#991b1b', fontWeight: '500', marginBottom: '6px' }}>
                  Invalid HTS code: Must be exactly 10 digits ({htsCode.replace(/\D/g, '').length}/10)
                </div>
              )}

              {/* HTS Validation Badges */}
              {htsCode.replace(/\D/g, '').length === 10 && (
                <>
                  {htsValidating && (
                    <div style={{ padding: '6px 8px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px', fontSize: '11px', color: '#6c757d', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                      <div style={{ width: '12px', height: '12px', border: '2px solid #dee2e6', borderTop: '2px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                      Validating via SSB...
                    </div>
                  )}

                  {!htsValidating && htsValidation && htsValidation.valid && (() => {
                    const hasAuthWarning = Boolean(htsValidation.authWarning)
                    const isAllowed = !hasAuthWarning && htsValidation.is_allowed !== false
                    const isPgaFda = htsValidation.is_pga_fda
                    const requiresMfr = htsValidation.requires_manufacturer_info
                    return (
                      <div style={{ marginBottom: '6px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '4px' }}>
                          <span style={{ fontSize: '10px', fontWeight: 700, color: '#fff', backgroundColor: isAllowed ? '#28a745' : '#856404', padding: '2px 6px', borderRadius: '3px', textTransform: 'uppercase' }}>
                            {hasAuthWarning ? 'Validation Unavailable' : isAllowed ? 'SSB Approved' : 'Action Required'}
                          </span>
                          {isPgaFda && <span style={{ fontSize: '10px', fontWeight: 700, color: '#fff', backgroundColor: '#1565c0', padding: '2px 6px', borderRadius: '3px', textTransform: 'uppercase' }}>PGA/FDA</span>}
                          {requiresMfr && <span style={{ fontSize: '10px', fontWeight: 700, color: '#fff', backgroundColor: '#ed6c02', padding: '2px 6px', borderRadius: '3px', textTransform: 'uppercase' }}>Mfr Required</span>}
                        </div>
                        {(hasAuthWarning || !isAllowed) && (
                          <div style={{ fontSize: '11px', fontWeight: 700, color: '#856404', padding: '4px 6px', backgroundColor: '#fffbe6', border: '1px solid #ffe58f', borderRadius: '3px', marginBottom: '4px' }}>
                            {hasAuthWarning ? 'SSB VALIDATION UNAVAILABLE' : 'NOT ALLOWED VIA SSB'}: <span style={{ fontWeight: 400, fontSize: '10px' }}>{htsValidation.warnings?.[0] || htsValidation.errors?.[0] || 'Retry validation before submission.'}</span>
                          </div>
                        )}
                        <div style={{ fontSize: '11px', color: isAllowed ? '#155724' : '#856404', lineHeight: 1.4 }}>
                          {htsValidation.fullDescription || htsValidation.description}
                        </div>
                      </div>
                    )
                  })()}

                  {!htsValidating && htsValidation && !htsValidation.valid && (
                    <div style={{ padding: '6px 8px', backgroundColor: '#f8d7da', border: '1px solid #dc2626', borderRadius: '4px', marginBottom: '6px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 600, color: '#721c24', marginBottom: '2px' }}>Invalid or Restricted HTS Code</div>
                      <div style={{ fontSize: '11px', color: '#721c24', lineHeight: 1.4 }}>{htsValidation.error || 'Not approved by SSB.'}</div>
                    </div>
                  )}
                </>
              )}

              <div style={{ fontSize: '10px', color: '#856404', lineHeight: 1.3, marginTop: '4px' }}>
                <strong>Disclaimer:</strong> HTS code suggestions are estimates only. Verify with customs brokers.
              </div>
            </div>

            {/* Right: Duty & SSB */}
            <div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Duty & Tariffs</div>

              {exchangeRate && item.declaredValue && item.declaredValue > 0 && (
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                  COGS: ${Number(item.declaredValue).toFixed(2)} CAD (${(Number(item.declaredValue) * exchangeRate.cadToUsd).toFixed(2)} USD)
                </div>
              )}

              {/* SSB Rate Check */}
              {!ssbVerifiedData ? (
                <button
                  onClick={handleCheckSSBRate}
                  disabled={ssbRateLoading || !htsCode || !countryOfOrigin || !item.declaredValue}
                  style={{ padding: '6px 12px', backgroundColor: '#f8fafc', color: '#1e293b', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '11px', fontWeight: 600, cursor: (ssbRateLoading || !htsCode || !countryOfOrigin || !item.declaredValue) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  {ssbRateLoading ? (
                    <><span style={{ display: 'inline-block', width: '12px', height: '12px', border: '2px solid #64748b', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></span> Checking SSB Rate...</>
                  ) : 'Check SSB Rate'}
                </button>
              ) : (
                <div style={{ padding: '8px 10px', backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '4px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#15803d' }}>{ssbVerifiedData.source === 'cusma' ? 'CUSMA Duty-Free' : 'SSB Verified'}</span>
                    <button onClick={() => { setSsbVerifiedData(null); onSSBRateCalculated?.(null); onDutyCalculated?.(0) }} style={{ padding: '2px 6px', fontSize: '10px', backgroundColor: 'transparent', border: '1px solid #bbf7d0', color: '#166534', borderRadius: '2px', cursor: 'pointer' }}>Clear</button>
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: '#1565c0' }}>${ssbVerifiedData.dutyAmount.toFixed(2)} CAD/unit ({ssbVerifiedData.dutyRate})</div>
                  <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>{ssbVerifiedData.source === 'cusma' ? 'US/CA/MX origin treated as CUSMA duty-free' : 'Verified via SSB'} at {new Date(ssbVerifiedData.timestamp).toLocaleTimeString()}</div>
                </div>
              )}
            </div>
          </div>

          {/* Customs Description Override (if set, show details) */}
          {customsDescriptionDraft && (
            <div style={{ marginBottom: '12px', padding: '8px 12px', backgroundColor: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '4px', fontSize: '12px', color: '#92400e', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Saved custom description override active</span>
              <button onClick={async () => {
                setCustomsDescriptionDraft('')
                onUpdate({ customsDescription: '' })
                await saveASINData({ customs_description: null })
                if (htsCode.replace(/\D/g, '').length === 10) {
                  await refreshFDAForManualChange(htsCode, '')
                }
              }} style={{ padding: '2px 8px', fontSize: '11px', border: 'none', backgroundColor: '#fef3c7', color: '#92400e', borderRadius: '4px', cursor: 'pointer', textDecoration: 'underline' }}>Clear</button>
            </div>
          )}

          {/* FDA Classification Result */}
          {fdaClassificationResult && (
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0f9ff', border: '2px solid #3b82f6', borderRadius: '6px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e40af', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>FDA Classification Found</span>
              </div>
              <div style={{ padding: '8px 12px', backgroundColor: 'white', borderRadius: '4px', marginBottom: '10px', border: '1px solid #bfdbfe' }}>
                <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px' }}>FDA Product Code</div>
                <div style={{ fontSize: '18px', fontWeight: 700, fontFamily: 'monospace', color: '#1e40af', letterSpacing: '1px' }}>{fdaClassificationResult.fdaCode}</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
                <div style={{ padding: '6px 8px', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #bfdbfe' }}>
                  <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>Industry ({fdaClassificationResult.classification?.industry})</div>
                  <div style={{ fontSize: '12px', fontWeight: 500, color: '#1e40af' }}>{fdaClassificationResult.industry?.split(' - ')[0] || 'N/A'}</div>
                </div>
                <div style={{ padding: '6px 8px', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #bfdbfe' }}>
                  <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>Class ({fdaClassificationResult.classification?.class})</div>
                  <div style={{ fontSize: '12px', fontWeight: 500, color: '#1e40af' }}>Product Class</div>
                </div>
                <div style={{ padding: '6px 8px', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #bfdbfe' }}>
                  <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>Subclass ({fdaClassificationResult.classification?.subclass})</div>
                  <div style={{ fontSize: '12px', fontWeight: 500, color: '#1e40af', lineHeight: 1.3 }}>{fdaClassificationResult.descriptions?.subclass || 'Packaging'}</div>
                </div>
                <div style={{ padding: '6px 8px', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #bfdbfe' }}>
                  <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>PIC ({fdaClassificationResult.classification?.pic})</div>
                  <div style={{ fontSize: '12px', fontWeight: 500, color: '#1e40af', lineHeight: 1.3 }}>{fdaClassificationResult.descriptions?.pic || 'Processing'}</div>
                </div>
              </div>
              <div style={{ padding: '8px', backgroundColor: 'white', borderRadius: '4px', marginBottom: '10px', border: '1px solid #bfdbfe' }}>
                <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '3px' }}>Product Classification</div>
                <div style={{ fontSize: '12px', color: '#374151', lineHeight: 1.4 }}>{fdaClassificationResult.product || 'N/A'}</div>
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button onClick={() => setFdaClassificationResult(null)} style={{ padding: '6px 12px', backgroundColor: 'white', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', fontWeight: 500 }}>Reject</button>
                <button onClick={async () => { setFdaProductCode(fdaClassificationResult.fdaCode); await saveASINData({ fda_product_code: fdaClassificationResult.fdaCode, is_food: true }); setFdaClassificationResult(null) }} style={{ padding: '6px 16px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', fontWeight: 600 }}>Accept & Save</button>
              </div>
            </div>
          )}

          {/* HTS Classification Loading - Professional Design */}
          {showHTSClassification && htsClassifying && (
            <div style={{
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              padding: '24px',
              marginTop: '16px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px'
              }}>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #e5e7eb',
                  borderTop: '2px solid #3b82f6',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <span style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#1f2937',
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }}>
                  Analyzing Product for HTS Classification
                </span>
              </div>

              <div style={{
                fontSize: '14px',
                color: '#6b7280',
                marginBottom: '16px',
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }}>
                {loadingMessage}
              </div>

              <div style={{
                width: '100%',
                height: '6px',
                backgroundColor: '#f3f4f6',
                borderRadius: '3px',
                overflow: 'hidden',
                marginBottom: '12px'
              }}>
                <div style={{
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, #3b82f6 0%, #10b981 50%, #3b82f6 100%)',
                  animation: 'loading-progress 2s ease-in-out infinite'
                }} />
              </div>

              <div style={{
                fontSize: '13px',
                color: '#9ca3af',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                lineHeight: '1.4'
              }}>
                Using AI to analyze product description and find the most accurate harmonized tariff schedule classification.
              </div>

            </div>
          )}

          {/* HTS Classification Results - Outside Grid */}
          {showHTSClassification && htsClassificationResults && !htsClassifying && (
            <div style={{
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              padding: '24px',
              marginTop: '16px'
            }}>
              {/* Professional Header */}
              <div style={{
                borderBottom: '1px solid #e5e7eb',
                paddingBottom: '16px',
                marginBottom: '20px'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '16px',
                  flexWrap: 'wrap'
                }}>
                  <div style={{ minWidth: 0, flex: '1 1 360px' }}>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#1f2937',
                      margin: '0 0 8px 0',
                      fontFamily: 'system-ui, -apple-system, sans-serif'
                    }}>
                      HTS Code Classification
                    </h3>
                    <p style={{
                      fontSize: '14px',
                      color: '#6b7280',
                      margin: 0,
                      fontFamily: 'system-ui, -apple-system, sans-serif'
                    }}>
                      AI-powered harmonized tariff schedule classification for US customs
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowHTSClassification(false)}
                    style={{
                      padding: '8px 12px',
                      fontSize: '13px',
                      fontWeight: '500',
                      border: '1px solid #d1d5db',
                      backgroundColor: '#ffffff',
                      color: '#374151',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontFamily: 'system-ui, -apple-system, sans-serif'
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>


              {/* Classification Results Grid */}
              <div style={{
                display: 'grid',
                gap: '16px',
                marginBottom: '24px'
              }}>
                {htsClassificationResults.htsCodes
                  .filter((option: any) => option.applicability !== 'not_applicable')
                  .slice(0, 10)
                  .map((option: any, index: number) => {
                    const optionCode = getHTSOptionCode(option)
                    const isSelected = normalizeHTSCode(htsCode).length === 10 && normalizeHTSCode(optionCode) === normalizeHTSCode(htsCode)

                    return (
                      <div
                        key={optionCode || index}
                        style={{
                          border: isSelected
                            ? '2px solid #0066cc'
                            : '1px solid #d1d5db',
                          borderRadius: '0',
                          padding: '20px',
                          backgroundColor: isSelected
                            ? '#f0f7ff'
                            : '#ffffff',
                          transition: 'border-color 0.1s',
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                        }}
                      >
                        {/* Header Row */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: '12px',
                          flexWrap: 'wrap',
                          marginBottom: '14px',
                          paddingBottom: '10px',
                          borderBottom: '1px solid #e5e7eb'
                        }}>
                          {/* Left: Rank and HTS Code */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: '1 1 360px', minWidth: 0 }}>
                            <div style={{
                              backgroundColor: isSelected ? '#0066cc' : '#6b7280',
                              color: '#ffffff',
                              width: '26px',
                              height: '26px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '13px',
                              fontWeight: '600',
                              flexShrink: 0
                            }}>
                              {index + 1}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 }}>
                              <a
                                href={`https://hts.usitc.gov/search?query=${(option.htsCode || '').replace(/\D/g, '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  fontSize: '17px',
                                  fontWeight: '600',
                                  color: '#0066cc',
                                  fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                  textDecoration: 'none',
                                  display: 'inline-block',
                                  overflowWrap: 'anywhere',
                                  borderBottom: '2px solid transparent',
                                  transition: 'border-color 0.2s'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.borderBottomColor = '#0066cc'}
                                onMouseOut={(e) => e.currentTarget.style.borderBottomColor = 'transparent'}
                                title="View on HTSUS website"
                              >
                                {option.formattedHtsCode || option.TariffNumFormatted || option.htsCode}
                              </a>
                              {option.fullTitle && (
                                <div style={{
                                  fontSize: '12px',
                                  fontWeight: '400',
                                  color: '#6b7280',
                                  lineHeight: '1.4',
                                  overflowWrap: 'anywhere'
                                }}>
                                  {option.fullTitle}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Right: Status Badges and Select Button */}
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', flex: '1 1 320px' }}>
                            {/* Applicability Badge */}
                            {option.applicability && (
                              <div style={{
                                padding: '3px 8px',
                                backgroundColor: option.applicability === 'definitely_applicable'
                                  ? '#dcfce7'
                                  : '#fef9c3',
                                border: `1px solid ${option.applicability === 'definitely_applicable'
                                  ? '#86efac'
                                  : '#fde047'}`,
                                fontSize: '10px',
                                fontWeight: '700',
                                color: option.applicability === 'definitely_applicable'
                                  ? '#15803d'
                                  : '#a16207',
                                letterSpacing: '0.3px',
                                textTransform: 'uppercase'
                              }}>
                                {option.applicability === 'definitely_applicable' ? '✓ DEFINITELY APPLICABLE' : '? MAY BE APPLICABLE'}
                              </div>
                            )}

                            <div style={{
                              padding: '3px 8px',
                              backgroundColor: (option.confidence || option.accuracyMatch) >= 85
                                ? '#dcfce7' : (option.confidence || option.accuracyMatch) >= 70
                                  ? '#fef9c3' : '#fee2e2',
                              border: `1px solid ${(option.confidence || option.accuracyMatch) >= 85
                                ? '#86efac' : (option.confidence || option.accuracyMatch) >= 70
                                  ? '#fde047' : '#fca5a5'}`,
                              fontSize: '11px',
                              fontWeight: '700',
                              color: (option.confidence || option.accuracyMatch) >= 85
                                ? '#15803d' : (option.confidence || option.accuracyMatch) >= 70
                                  ? '#a16207' : '#991b1b',
                              letterSpacing: '0.3px'
                            }}>
                              {option.confidence || option.accuracyMatch}% MATCH
                            </div>

                            {/* Not Allowed Warning Badge */}
                            {option.isAllowed === false && (
                              <div style={{
                                padding: '3px 8px',
                                backgroundColor: '#fee2e2',
                                border: '1px solid #fca5a5',
                                fontSize: '10px',
                                fontWeight: '700',
                                color: '#991b1b',
                                letterSpacing: '0.3px'
                              }}>
                                NOT ALLOWED
                              </div>
                            )}

                            {/* Select Button in Header */}
                            <button
                              type="button"
                              onMouseDown={async (e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                await selectHTSCode(option)
                              }}
                              style={{
                                padding: '4px 12px',
                                fontSize: '11px',
                                fontWeight: '700',
                                border: isSelected ? '2px solid #16a34a' : '1px solid #0066cc',
                                backgroundColor: isSelected ? '#16a34a' : '#0066cc',
                                color: '#ffffff',
                                cursor: 'pointer',
                                letterSpacing: '0.3px',
                                textTransform: 'uppercase',
                                transition: 'background-color 0.15s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = isSelected ? '#15803d' : '#0052a3'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = isSelected ? '#16a34a' : '#0066cc'
                              }}
                            >
                              {isSelected ? '✓ SELECTED' : 'SELECT'}
                            </button>
                          </div>
                        </div>

                        {/* Classification Reasoning */}
                        {option.reasoning && (
                          <div style={{
                            marginBottom: '12px',
                            padding: '12px',
                            backgroundColor: '#f9fafb',
                            border: '1px solid #e5e7eb',
                            borderLeft: '3px solid #0066cc'
                          }}>
                            <div style={{
                              fontWeight: '600',
                              marginBottom: '6px',
                              color: '#111827',
                              fontSize: '12px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              Classification Reasoning
                            </div>
                            <div style={{
                              fontSize: '13px',
                              color: '#4b5563',
                              lineHeight: '1.6'
                            }}>
                              {(() => {
                                // Convert HTS codes in reasoning to clickable links
                                const reasoning = option.reasoning;
                                // Match HTS codes in format XXXX.XX.XX.XX or XXXX.XX.XX or variations with/without dots
                                const htsCodeRegex = /\b(\d{4}[\.\s]?\d{2}[\.\s]?\d{2}(?:[\.\s]?\d{2})?)\b/g;
                                const parts = [];
                                let lastIndex = 0;
                                let match;

                                while ((match = htsCodeRegex.exec(reasoning)) !== null) {
                                  // Add text before the match
                                  if (match.index > lastIndex) {
                                    parts.push(reasoning.substring(lastIndex, match.index));
                                  }

                                  // Add clickable HTS code link
                                  const htsCode = match[1].replace(/[\s\.]/g, ''); // Remove spaces and dots for URL
                                  const formattedCode = match[1]; // Keep original format for display
                                  parts.push(
                                    <a
                                      key={match.index}
                                      href={`https://hts.usitc.gov/search?query=${htsCode}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        color: '#0066cc',
                                        textDecoration: 'none',
                                        fontWeight: '600',
                                        borderBottom: '1px solid #0066cc'
                                      }}
                                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f0f7ff'}
                                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    >
                                      {formattedCode}
                                    </a>
                                  );

                                  lastIndex = match.index + match[0].length;
                                }

                                // Add remaining text
                                if (lastIndex < reasoning.length) {
                                  parts.push(reasoning.substring(lastIndex));
                                }

                                return parts.length > 0 ? parts : reasoning;
                              })()}
                            </div>
                          </div>
                        )}

                        {/* Estimated Duty and Tariff */}
                        {(option.dutyAmount !== undefined || option.dutyRate) && (
                          <div
                            key={`duty-${option.htsCode || option.TariffNum}-${cusmaApplicable}`}
                            style={{
                              marginBottom: '12px',
                              padding: '14px',
                              backgroundColor: cusmaApplicable || option.dutyAmount === 0 || option.dutyRate === 'Free' || option.dutyRate === '0.0%' ? '#f0fdf4' : '#fef2f2',
                              border: cusmaApplicable || option.dutyAmount === 0 || option.dutyRate === 'Free' || option.dutyRate === '0.0%' ? '1px solid #86efac' : '1px solid #dc2626',
                              borderLeft: cusmaApplicable || option.dutyAmount === 0 || option.dutyRate === 'Free' || option.dutyRate === '0.0%' ? '4px solid #15803d' : '4px solid #dc2626'
                            }}>
                            <div style={{
                              fontWeight: '600',
                              marginBottom: '8px',
                              color: cusmaApplicable || option.dutyAmount === 0 || option.dutyRate === 'Free' || option.dutyRate === '0.0%' ? '#15803d' : '#dc2626',
                              fontSize: '12px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              {cusmaApplicable ? 'CUSMA DUTY-FREE' : option.dutyAmount === 0 || option.dutyRate === 'Free' || option.dutyRate === '0.0%' ? 'POSSIBLY DUTY-FREE' : option.dutyAmount !== undefined ? 'ESTIMATED DUTY & TARIFF' : 'DUTY RATE'}
                            </div>
                            {cusmaApplicable || (option.dutyAmount !== undefined && option.dutyAmount === 0) ? (
                              // Show CUSMA duty-free message OR zero duty
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: '80px 1fr',
                                gap: '8px 12px',
                                fontSize: '13px'
                              }}>
                                <div style={{ color: '#15803d', fontWeight: '500' }}>Rate:</div>
                                <div style={{ color: '#15803d', fontWeight: '600' }}>0% {cusmaApplicable ? '(CUSMA)' : ''}</div>

                                <div style={{ color: '#15803d', fontWeight: '500' }}>Per Unit:</div>
                                <div style={{ color: '#15803d', fontWeight: '700', fontSize: '15px' }}>
                                  $0.00 CAD
                                </div>

                                {item.quantity > 1 && (
                                  <>
                                    <div style={{ color: '#15803d', fontWeight: '500' }}>Total:</div>
                                    <div style={{ color: '#15803d', fontWeight: '700', fontSize: '15px' }}>
                                      $0.00 CAD (Qty: {item.quantity})
                                    </div>
                                  </>
                                )}
                              </div>
                            ) : option.dutyAmount !== undefined ? (
                              // Show calculated duty amount (when COGS is available)
                              option.dutyAmount > 0 ? (
                                <div style={{
                                  display: 'grid',
                                  gridTemplateColumns: '80px 1fr',
                                  gap: '8px 12px',
                                  fontSize: '13px'
                                }}>
                                  <div style={{ color: '#991b1b', fontWeight: '500' }}>Rate:</div>
                                  <div style={{ color: '#7f1d1d', fontWeight: '600' }}>{option.dutyPercentage}%</div>

                                  <div style={{ color: '#991b1b', fontWeight: '500' }}>Per Unit:</div>
                                  <div style={{ color: '#7f1d1d', fontWeight: '700', fontSize: '15px' }}>
                                    ${option.dutyAmount.toFixed(2)} CAD
                                  </div>

                                  {item.quantity > 1 && (
                                    <>
                                      <div style={{ color: '#991b1b', fontWeight: '500' }}>Total:</div>
                                      <div style={{ color: '#7f1d1d', fontWeight: '700', fontSize: '15px' }}>
                                        ${(option.dutyAmount * item.quantity).toFixed(2)} CAD (Qty: {item.quantity})
                                      </div>
                                    </>
                                  )}
                                </div>
                              ) : (
                                <div style={{
                                  fontSize: '12px',
                                  color: '#854d0e',
                                  fontWeight: '400',
                                  lineHeight: '1.6'
                                }}>
                                  The shipping broker is not showing any duty rate when this HTS code is inputted, so it may be duty-free. Please externally double-check to confirm before finalizing.
                                </div>
                              )
                            ) : option.dutyRate ? (
                              // Show duty rate only (when COGS not available but duty rate is known)
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: '80px 1fr',
                                gap: '8px 12px',
                                fontSize: '13px'
                              }}>
                                <div style={{ color: '#991b1b', fontWeight: '500' }}>Rate:</div>
                                <div style={{ color: '#7f1d1d', fontWeight: '600' }}>{option.dutyRate}</div>

                                {/* Hint for country of origin */}
                                {(option as any).needsCountryForAccurateDuty && (
                                  <div style={{ gridColumn: '1 / -1', fontSize: '11px', color: '#92400e', fontStyle: 'italic', marginTop: '4px', backgroundColor: '#fef3c7', padding: '6px 8px', borderRadius: '4px' }}>
                                    Select country of origin above for more accurate duty calculation
                                  </div>
                                )}

                                {/* Hint for COGS */}
                                {(!item.declaredValue || item.declaredValue === 0) && (
                                  <div style={{ gridColumn: '1 / -1', fontSize: '11px', color: '#6b7280', fontStyle: 'italic', marginTop: '4px' }}>
                                    Enter COGS (Cost of Goods Sold) above to see calculated duty amount
                                  </div>
                                )}
                              </div>
                            ) : null}
                          </div>
                        )}

                      </div>
                    )
                  })}
              </div>

              {/* Success Message */}
              <div style={{
                padding: '16px',
                backgroundColor: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '6px'
              }}>
                <h4 style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#166534',
                  margin: '0 0 8px 0',
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }}>
                  Classification Complete
                </h4>
                <p style={{
                  fontSize: '13px',
                  color: '#166534',
                  margin: 0,
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }}>
                  Found {htsClassificationResults.htsCodes.length} potential HTS codes. Select the most appropriate classification above.
                </p>
              </div>
            </div>
          )}

          {/* Manufacturing Address Section */}
          {item.asin && (
            <div style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#fff',
              borderRadius: '4px',
              border: '1px solid #e0e0e0'
            }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                  Manufacturing Address
                </label>
                <div className="flex gap-2 mb-2">
                  <select
                    value={selectedAddressId || ''}
                    onChange={async (e) => {
                      if (e.target.value) {
                        handleAddressSelect(parseInt(e.target.value))
                      } else {
                        setSelectedAddressId(null)
                        setManufacturingAddress(undefined)
                        setCountryMismatchError(null) // Clear error when no address selected

                        // Update parent component
                        onUpdate({
                          manufacturingAddress: undefined
                        })

                        // Save the deselection
                        await saveASINData({
                          manufacturing_address: undefined,
                          manufacturing_address_id: null
                        })
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '6px 8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select from previous addresses...</option>
                    {localManufacturingAddresses.map(addr => (
                      <option key={addr.id} value={addr.id}>
                        {addr.is_food ? '[Food] ' : ''}{addr.name} - {addr.city}, {addr.country}{addr.is_food && addr.fda_registration_id ? ` (FDA: ${addr.fda_registration_id})` : ''}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => setShowNewAddressForm(!showNewAddressForm)}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #0ea5e9',
                      backgroundColor: showNewAddressForm ? '#0ea5e9' : '#fff',
                      color: showNewAddressForm ? '#fff' : '#0ea5e9',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    {showNewAddressForm ? 'Cancel' : 'Add New'}
                  </button>
                </div>

                {/* Selected Address Display */}
                {selectedAddressId && (() => {
                  const selectedAddress = localManufacturingAddresses.find(addr => addr.id === selectedAddressId)
                  return selectedAddress ? (
                    <div style={{
                      padding: '12px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px',
                      fontSize: '14px',
                      marginBottom: '8px',
                      border: '1px solid #e9ecef'
                    }}>
                      {/* Address Header with Confidence */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <strong style={{ fontSize: '15px', color: '#333' }}>{selectedAddress.name}</strong>
                          {selectedAddress.is_food && (
                            <span style={{
                              fontSize: '11px',
                              fontWeight: '600',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              backgroundColor: '#dbeafe',
                              color: '#1e40af'
                            }}>
                              Food Facility
                            </span>
                          )}
                        </div>
                        {selectedAddress.confidence && (
                          <span style={{
                            fontSize: '11px',
                            fontWeight: '600',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            backgroundColor: selectedAddress.confidence >= 4 ? '#dcfce7' : selectedAddress.confidence >= 3 ? '#fef3c7' : '#fee2e2',
                            color: selectedAddress.confidence >= 4 ? '#166534' : selectedAddress.confidence >= 3 ? '#92400e' : '#991b1b'
                          }}>
                            Confidence: {selectedAddress.confidence}/5
                          </span>
                        )}
                      </div>

                      {/* FDA Registration ID */}
                      {selectedAddress.is_food && selectedAddress.fda_registration_id && (
                        <div style={{
                          fontSize: '12px',
                          color: '#475569',
                          marginBottom: '8px',
                          padding: '4px 8px',
                          backgroundColor: '#f1f5f9',
                          borderRadius: '3px',
                          borderLeft: '3px solid #3b82f6'
                        }}>
                          <strong>FDA Registration ID:</strong> {selectedAddress.fda_registration_id}
                        </div>
                      )}

                      {/* Address Details */}
                      <div style={{ marginBottom: '8px' }}>
                        {selectedAddress.street_address}<br />
                        {selectedAddress.city}{selectedAddress.state && `, ${selectedAddress.state}`}<br />
                        {selectedAddress.country} {selectedAddress.zip_code}
                      </div>

                      {/* Food Facility Edit Section */}
                      <div style={{
                        marginBottom: '12px',
                        padding: '12px',
                        backgroundColor: selectedAddress.is_food ? '#f0f9ff' : '#f8fafc',
                        borderRadius: '6px',
                        border: selectedAddress.is_food ? '2px solid #3b82f6' : '1px solid #e2e8f0'
                      }}>
                        <label style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          fontSize: '14px',
                          cursor: 'pointer',
                          marginBottom: selectedAddress.is_food ? '12px' : '0',
                          fontWeight: '500'
                        }}>
                          <input
                            type="checkbox"
                            checked={selectedAddress.is_food || false}
                            onChange={async (e) => {
                              const isFood = e.target.checked
                              try {
                                await apiService.updateClientManufacturingAddress(selectedAddress.id!, {
                                  is_food: isFood,
                                  fda_registration_id: isFood ? selectedAddress.fda_registration_id : null
                                })
                                // Update local state
                                const updatedAddresses = localManufacturingAddresses.map(addr =>
                                  addr.id === selectedAddress.id
                                    ? { ...addr, is_food: isFood, fda_registration_id: isFood ? addr.fda_registration_id : undefined }
                                    : addr
                                )
                                setLocalManufacturingAddresses(updatedAddresses)
                              } catch (error) {
                                console.error('Error updating food facility status:', error)
                                alert('Failed to update food facility status')
                              }
                            }}
                            style={{
                              cursor: 'pointer',
                              width: '16px',
                              height: '16px'
                            }}
                          />
                          <span style={{
                            color: selectedAddress.is_food ? '#1e40af' : '#475569',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            {selectedAddress.is_food && <span style={{ fontSize: '13px' }}>Food</span>}
                            Food Manufacturing Facility
                          </span>
                        </label>

                        {selectedAddress.is_food && (
                          <div style={{
                            marginLeft: '26px',
                            paddingLeft: '12px',
                            borderLeft: '3px solid #3b82f6'
                          }}>
                            <label style={{
                              display: 'block',
                              fontSize: '12px',
                              fontWeight: '600',
                              color: '#334155',
                              marginBottom: '6px'
                            }}>
                              FDA Food Facility Registration ID
                            </label>
                            <input
                              type="text"
                              placeholder="Enter 7-11 digit FDA Registration ID"
                              value={selectedAddress.fda_registration_id || ''}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '').slice(0, 11)
                                // Update local state immediately for responsive UI
                                const updatedAddresses = localManufacturingAddresses.map(addr =>
                                  addr.id === selectedAddress.id
                                    ? { ...addr, fda_registration_id: value }
                                    : addr
                                )
                                setLocalManufacturingAddresses(updatedAddresses)
                              }}
                              onBlur={async (e) => {
                                // Save to backend on blur
                                const fdaId = e.target.value.trim()
                                if (fdaId && (fdaId.length < 7 || fdaId.length > 11)) {
                                  alert('FDA Registration ID must be 7-11 digits')
                                  return
                                }
                                try {
                                  await apiService.updateClientManufacturingAddress(selectedAddress.id!, {
                                    fda_registration_id: fdaId || null
                                  })
                                } catch (error) {
                                  console.error('Error updating FDA ID:', error)
                                  alert('Failed to update FDA Registration ID')
                                }
                              }}
                              maxLength={11}
                              style={{
                                padding: '8px 10px',
                                border: '1px solid #cbd5e1',
                                borderRadius: '4px',
                                fontSize: '13px',
                                width: '100%',
                                maxWidth: '280px',
                                fontFamily: 'monospace',
                                letterSpacing: '0.5px'
                              }}
                            />
                            <div style={{
                              fontSize: '11px',
                              color: (selectedAddress.fda_registration_id && selectedAddress.fda_registration_id.length >= 7 && selectedAddress.fda_registration_id.length <= 11) ? '#16a34a' : '#64748b',
                              marginTop: '4px',
                              fontWeight: '500'
                            }}>
                              {selectedAddress.fda_registration_id?.length || 0}/11 digits
                              {(selectedAddress.fda_registration_id && selectedAddress.fda_registration_id.length >= 7 && selectedAddress.fda_registration_id.length <= 11) && ' ✓ Valid'}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Research Analysis */}
                      {selectedAddress.reasoning && (
                        <div style={{
                          fontSize: '12px',
                          color: '#6c757d',
                          fontStyle: 'italic',
                          marginBottom: '8px',
                          padding: '6px 8px',
                          backgroundColor: '#f1f3f4',
                          borderRadius: '3px',
                          lineHeight: '1.4'
                        }}>
                          <strong style={{ fontStyle: 'normal', color: '#555' }}>Research Analysis:</strong><br />
                          {selectedAddress.reasoning}
                        </div>
                      )}

                      {/* Sources */}
                      {selectedAddress.sources && selectedAddress.sources.length > 0 && (
                        <div style={{
                          fontSize: '11px',
                          marginTop: '8px'
                        }}>
                          <strong style={{ color: '#555', fontSize: '12px' }}>Research Sources ({selectedAddress.sources.length}):</strong>
                          {selectedAddress.sources.map((source, index) => (
                            <div key={index} style={{
                              marginTop: '4px',
                              padding: '4px 6px',
                              backgroundColor: '#e9ecef',
                              borderRadius: '3px'
                            }}>
                              <div style={{ fontWeight: '500', marginBottom: '2px' }}>
                                {source.description}
                              </div>
                              {source.url && (
                                <div>
                                  <strong>Source:</strong>
                                  <a
                                    href={source.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: '#0066cc',
                                      textDecoration: 'none',
                                      marginLeft: '4px'
                                    }}
                                  >
                                    {source.url.length > 50 ? source.url.substring(0, 50) + '...' : source.url}
                                  </a>
                                </div>
                              )}
                              <div style={{ color: '#6c757d', marginTop: '2px' }}>
                                Type: {source.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : null
                })()}

                {/* AI Address Suggestion */}
                {aiSuggestedAddress && (
                  <div style={{
                    padding: '12px',
                    border: '2px solid #0ea5e9',
                    borderRadius: '4px',
                    backgroundColor: '#f8f9fa',
                    marginBottom: '8px'
                  }}>
                    {/* Header with Confidence */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <h4 style={{ margin: 0, fontSize: '14px', color: '#0ea5e9' }}>
                        AI Found Manufacturing Address
                      </h4>
                      {aiSuggestedAddress.confidence && (
                        <span style={{
                          fontSize: '11px',
                          fontWeight: '600',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          backgroundColor: aiSuggestedAddress.confidence >= 4 ? '#dcfce7' : aiSuggestedAddress.confidence >= 3 ? '#fef3c7' : '#fee2e2',
                          color: aiSuggestedAddress.confidence >= 4 ? '#166534' : aiSuggestedAddress.confidence >= 3 ? '#92400e' : '#991b1b'
                        }}>
                          Confidence: {aiSuggestedAddress.confidence}/5
                        </span>
                      )}
                    </div>

                    {/* Address Details */}
                    <div style={{ fontSize: '13px', marginBottom: '8px' }}>
                      <strong>{aiSuggestedAddress.name}</strong><br />
                      {aiSuggestedAddress.street_address}<br />
                      {aiSuggestedAddress.city}{aiSuggestedAddress.state && `, ${aiSuggestedAddress.state}`}<br />
                      {aiSuggestedAddress.country} {aiSuggestedAddress.zip_code}
                    </div>


                    {/* Research Analysis */}
                    {aiSuggestedAddress.reasoning && (
                      <div style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        fontStyle: 'italic',
                        marginBottom: '8px',
                        padding: '6px 8px',
                        backgroundColor: '#e3f2fd',
                        borderRadius: '3px',
                        lineHeight: '1.4'
                      }}>
                        <strong style={{ fontStyle: 'normal', color: '#0277bd' }}>Research Analysis:</strong><br />
                        {aiSuggestedAddress.reasoning}
                      </div>
                    )}

                    {/* Sources */}
                    {aiSuggestedAddress.sources && aiSuggestedAddress.sources.length > 0 && (
                      <div style={{
                        fontSize: '11px',
                        marginBottom: '8px'
                      }}>
                        <strong style={{ color: '#0277bd', fontSize: '12px' }}>Research Sources ({aiSuggestedAddress.sources.length}):</strong>
                        {aiSuggestedAddress.sources.map((source, index) => (
                          <div key={index} style={{
                            marginTop: '4px',
                            padding: '4px 6px',
                            backgroundColor: '#e1f5fe',
                            borderRadius: '3px'
                          }}>
                            <div style={{ fontWeight: '500', marginBottom: '2px' }}>
                              {source.description}
                            </div>
                            {source.url && (
                              <div>
                                <strong>Source:</strong>
                                <a
                                  href={source.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: '#0066cc',
                                    textDecoration: 'none',
                                    marginLeft: '4px'
                                  }}
                                >
                                  {source.url.length > 50 ? source.url.substring(0, 50) + '...' : source.url}
                                </a>
                              </div>
                            )}
                            <div style={{ color: '#6c757d', marginTop: '2px' }}>
                              Type: {source.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      <button
                        onClick={acceptAISuggestion}
                        disabled={saving}
                        style={{
                          padding: '4px 8px',
                          border: 'none',
                          backgroundColor: saving ? '#6c757d' : '#28a745',
                          color: '#fff',
                          borderRadius: '3px',
                          fontSize: '12px',
                          cursor: saving ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {saving ? 'Saving...' : 'Accept & Use'}
                      </button>
                      <button
                        onClick={rejectAISuggestion}
                        style={{
                          padding: '4px 8px',
                          border: '1px solid #dc2626',
                          backgroundColor: '#fff',
                          color: '#dc2626',
                          borderRadius: '3px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                )}

                {/* New Address Form */}
                {showNewAddressForm && (
                  <div style={{
                    padding: '12px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    backgroundColor: '#fff'
                  }}>

                    {/* Address Form Fields */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                      gap: '8px'
                    }}>
                      <input
                        type="text"
                        placeholder="Company Name"
                        value={newAddress.name}
                        onChange={(e) => setNewAddress({ ...newAddress, name: e.target.value })}
                        style={{ padding: '6px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                      />
                      <input
                        type="text"
                        placeholder="Street Address"
                        value={newAddress.street_address}
                        onChange={(e) => setNewAddress({ ...newAddress, street_address: e.target.value })}
                        style={{ padding: '6px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                      />
                      <input
                        type="text"
                        placeholder="City"
                        value={newAddress.city}
                        onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                        style={{ padding: '6px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                      />
                      <input
                        type="text"
                        placeholder="State"
                        value={newAddress.state}
                        onChange={(e) => setNewAddress({ ...newAddress, state: e.target.value })}
                        style={{ padding: '6px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                      />
                      <select
                        value={newAddress.country}
                        onChange={(e) => setNewAddress({ ...newAddress, country: e.target.value })}
                        style={{ padding: '6px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                      >
                        {COUNTRIES.map(country => (
                          <option key={country.code} value={country.code}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        placeholder="Zip Code"
                        value={newAddress.zip_code}
                        onChange={(e) => setNewAddress({ ...newAddress, zip_code: e.target.value })}
                        style={{ padding: '6px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                      />

                      {/* Food Facility Tracking */}
                      <div style={{
                        gridColumn: '1 / -1',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        marginTop: '8px',
                        padding: '8px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '4px'
                      }}>
                        <label style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '14px',
                          cursor: 'pointer'
                        }}>
                          <input
                            type="checkbox"
                            checked={newAddress.is_food || false}
                            onChange={(e) => setNewAddress({
                              ...newAddress,
                              is_food: e.target.checked,
                              fda_registration_id: e.target.checked ? newAddress.fda_registration_id : undefined
                            })}
                            style={{ cursor: 'pointer' }}
                          />
                          <span>This facility manufactures food items</span>
                        </label>

                        {newAddress.is_food && (
                          <div style={{ marginLeft: '24px' }}>
                            <input
                              type="text"
                              placeholder="FDA Registration ID (7-11 digits)"
                              value={newAddress.fda_registration_id || ''}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '').slice(0, 11)
                                setNewAddress({ ...newAddress, fda_registration_id: value })
                              }}
                              maxLength={11}
                              style={{
                                padding: '6px 8px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px',
                                width: '250px'
                              }}
                            />
                            <div style={{
                              fontSize: '11px',
                              color: (newAddress.fda_registration_id && newAddress.fda_registration_id.length >= 7 && newAddress.fda_registration_id.length <= 11) ? '#198754' : '#6c757d',
                              marginTop: '4px'
                            }}>
                              {newAddress.fda_registration_id?.length || 0}/11 digits
                              {(newAddress.fda_registration_id && newAddress.fda_registration_id.length >= 7 && newAddress.fda_registration_id.length <= 11) && ' ✓'}
                            </div>
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
                        <button
                          onClick={lookupManufacturingAddressWithAI}
                          disabled={aiLookupLoading || !countryOfOrigin}
                          style={{
                            padding: '6px 12px',
                            border: '1px solid #0ea5e9',
                            backgroundColor: aiLookupLoading ? '#6c757d' : '#0ea5e9',
                            color: '#fff',
                            borderRadius: '4px',
                            fontSize: '14px',
                            cursor: aiLookupLoading || !countryOfOrigin ? 'not-allowed' : 'pointer',
                            opacity: !countryOfOrigin ? 0.5 : 1
                          }}
                        >
                          {aiLookupLoading ? loadingMessage : 'AI Lookup Address'}
                        </button>
                        <button
                          onClick={saveNewManufacturingAddress}
                          disabled={!newAddress.name || !newAddress.street_address || !newAddress.city}
                          style={{
                            padding: '6px 12px',
                            border: 'none',
                            backgroundColor: '#198754',
                            color: '#fff',
                            borderRadius: '4px',
                            fontSize: '14px',
                            opacity: (!newAddress.name || !newAddress.street_address || !newAddress.city) ? 0.5 : 1,
                            cursor: (!newAddress.name || !newAddress.street_address || !newAddress.city) ? 'not-allowed' : 'pointer'
                          }}
                        >
                          Save Address
                        </button>
                      </div>

                      {/* Manufacturing Address Disclaimer */}
                      <div style={{
                        gridColumn: '1 / -1',
                        backgroundColor: '#fff3cd',
                        border: '1px solid #ffeaa7',
                        borderRadius: '4px',
                        padding: '8px',
                        marginTop: '8px',
                        fontSize: '11px',
                        color: '#856404',
                        lineHeight: '1.3'
                      }}>
                        <strong>Disclaimer:</strong> Manufacturing address suggestions are estimates only. You must verify accuracy and compliance with customs regulations.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {saving && (
            <div style={{ fontSize: '12px', color: '#0ea5e9', marginTop: '8px' }}>
              Saving changes...
            </div>
          )}
        </div>
      )}
    </>
  )
}
